// Page module: the live scoresheet (matches#show). The bulk of
// this file is initAfterLoad, which builds the Twine context holding
// every scoring action.

global.scoresheet = {
  // Bumped by refreshContext (general/context.js) — one number that says "the screen
  // is being redrawn". context.feasibleLogs keys its cache on it so the filtered log
  // is built once per redraw rather than once per row. Deliberately NOT tied to
  // saving: the log table has to follow the keeper on a dead connection.
  uiTick: 0,
  // `filters` belongs in the key as much as the tick does: the filter selects are
  // TWO-WAY twine bindings, so picking one writes straight into the context and
  // refreshes the bindings — without ever going through refreshContext. With the
  // tick alone the cache answered every one of those redraws with the rows from
  // before the pick, and changing a filter did visibly nothing.
  logCache: { tick: -1, filters: null, source: null, length: -1, rows: [] },

  data: {
    state: {
      FT_PRESSED: "FT_PRESSED",
      PT2_PRESSED: "PT2_PRESSED",
      PT3_PRESSED: "PT3_PRESSED",
      FOUL_PRESSED: "FOUL_PRESSED",
      FTFOUL_PRESSED: "FTFOUL_PRESSED",
      UFOUL_PRESSED: "UFOUL_PRESSED",
      TFOUL_PRESSED: "TFOUL_PRESSED",
      // the FIBA split: each half of the old nešportna and tehnična is its own
      // state, because each is its own log fn — see Constant::FOUL_KINDS
      FLAGRANT_PRESSED: "FLAGRANT_PRESSED",
      DISRUPTIVE_PRESSED: "DISRUPTIVE_PRESSED",
      TECH1_PRESSED: "TECH1_PRESSED",
      TECH2_PRESSED: "TECH2_PRESSED",
      EJECTION_PRESSED: "EJECTION_PRESSED",
      FIGHT_PRESSED: "FIGHT_PRESSED",
      PLAYER_SELECTED: "PLAYER_SELECTED",
      COACH_SELECTED: "COACH_SELECTED",
      ASSISTANT_SELECTED: "ASSISTANT_SELECTED",
      OTHER_SELECTED: "OTHER_SELECTED",
      TIMEOUT_TAKEN: "TIMEOUT_TAKEN",
      REMOVE_TIMEOUT: "REMOVE_TIMEOUT",
      CHANGE_CAPTAIN: "CHANGE_CAPTAIN",
      CHALLENGE_TAKEN: "CHALLENGE_TAKEN"
    },
    // Display names for the composer's state. These are what the ink line shows while
    // an entry is being made, so they must describe what is being recorded, not its
    // outcome — whether a free throw went in is decided by the Zadet/Zgrešen buttons
    // several steps later, and "Zadet prosti met" therefore lied for the whole flow.
    stateName: {
      FT_PRESSED: "Prosti met",
      PT2_PRESSED: "Zadet met za 2 točki",
      PT3_PRESSED: "Zadet met za 3 točke",
      // Short, because these are read in a 216px slot: the full "Osebna napaka brez
      // prostih metov" needs 343 and came out as "Osebna napaka bre…" — the commonest
      // entry on the sheet, truncated in the one band that exists to say what is being
      // written. Both halves of the choice are shortened together; one long and one
      // short read as a bug.
      FOUL_PRESSED: "Os. napaka brez",
      FTFOUL_PRESSED: "Os. napaka s",
      // "Nešportna", not "Groba": groba osebna napaka is FLAGRANT_PRESSED below, a
      // different foul with a different consequence (izključitev takoj, against two
      // nešportni). Only "osebna" is abbreviated here.
      UFOUL_PRESSED: "Nešportna os. napaka",
      TFOUL_PRESSED: "Tehnična napaka",
      FLAGRANT_PRESSED: "Groba os. napaka",
      DISRUPTIVE_PRESSED: "Taktična os. napaka",
      TECH1_PRESSED: "Teh. napaka - kat. 1",
      TECH2_PRESSED: "Teh. napaka - kat. 2",
      EJECTION_PRESSED: "Izključitev",   // the button, the state and the log now agree
      FIGHT_PRESSED: "Pretep",
      PLAYER_SELECTED: "Igralec izbran",
      COACH_SELECTED: "Trener je izbran",
      ASSISTANT_SELECTED: "Pomočnik trenerja je izbran",
      OTHER_SELECTED: "Nekdo od ostalih je izbran",
      TIMEOUT_TAKEN: "Minuta odmora",
      REMOVE_TIMEOUT: "Odvzeta min. odm.",
      CHANGE_CAPTAIN: "Zamenjava kapetana",
      CHALLENGE_TAKEN: "HCC"
    },
    stateNameExtra: {
      FT_PRESSED: "Izberi igralca...",
      PT2_PRESSED: "Izberi igralca...",
      PT3_PRESSED: "Izberi igralca...",
      FOUL_PRESSED: "Izberi igralca...",
      FTFOUL_PRESSED: "Izberi igralca...",
      UFOUL_PRESSED: "Izberi igralca...",
      TFOUL_PRESSED: "Izberi igralca, trenerja, pomočnika ali ostale...",
      FLAGRANT_PRESSED: "Izberi igralca...",
      DISRUPTIVE_PRESSED: "Izberi igralca...",
      TECH1_PRESSED: "Izberi igralca, trenerja, pomočnika ali ostale...",
      TECH2_PRESSED: "Izberi igralca, trenerja, pomočnika ali ostale...",
      EJECTION_PRESSED: "Izberi igralca, trenerja, pomočnika ali ostale...",
      FIGHT_PRESSED: "Izberi igralca...",
      PLAYER_SELECTED: "Izberi dogodek...",
      COACH_SELECTED: "Izberi dogodek...",
      ASSISTANT_SELECTED: "Izberi dogodek...",
      OTHER_SELECTED: "Izberi dogodek...",
      TIMEOUT_TAKEN: "Izberi minuto ali potrdi minuto s preslednico...",
      REMOVE_TIMEOUT: "Izberi minuto ali potrdi minuto s preslednico...",
      CHANGE_CAPTAIN: "Izberi igralca, ki bo novi kapetan...",
      CHALLENGE_TAKEN: "Izberi minuto ali potrdi minuto s preslednico...",
    },
    logs: {
      withMinute: function(minute, q, str) {
        return scoresheet.quarterName(q) + " | " + minute + "' | " + str;
      },
      home: 'Domači',
      away: 'Gosti',
      coach: 'Trener',
      other: 'Ostali na klopi',
      assistant: "Pomočnik",
      actions: {
        changeFouls: {
          name: function(log) { return "Os. napaka brez pr. metov"; },
          text: function(log) {
            return scoresheet.data.logs.withMinute(log.minute, log.quarter, scoresheet.playerNameWithNumber(log.pid) + " je storil osebno napako. Ni prostih metov.");
          }
        },
        changeFTFouls: {
          name: function(log) { return "Os. napaka s pr. meti"; },
          text: function(log) {
            return scoresheet.data.logs.withMinute(log.minute, log.quarter, scoresheet.playerNameWithNumber(log.pid) + " je storil osebno napako. Sledijo prosti meti.");
          }
        },
        changeTFouls: {
          name: function(log) { return "Teh. napaka"; },
          text: function(log) {
            return scoresheet.data.logs.withMinute(log.minute, log.quarter, scoresheet.playerNameWithNumber(log.pid) + " je prejel tehnično napako.");
          }
        },
        changeUFouls: {
          name: function(log) { return "Nešp. napaka"; },
          text: function(log) {
            return scoresheet.data.logs.withMinute(log.minute, log.quarter, scoresheet.playerNameWithNumber(log.pid) + " je storil nešportno osebno napako.");
          }
        },
        changeFlagrantFouls: {
          name: function(log) { return "Groba napaka"; },
          text: function(log) {
            return scoresheet.data.logs.withMinute(log.minute, log.quarter, scoresheet.playerNameWithNumber(log.pid) + " je storil grobo osebno napako.");
          }
        },
        changeDisruptiveFouls: {
          name: function(log) { return "Taktična napaka"; },
          text: function(log) {
            return scoresheet.data.logs.withMinute(log.minute, log.quarter, scoresheet.playerNameWithNumber(log.pid) + " je storil taktično osebno napako.");
          }
        },
        changeTech1Fouls: {
          name: function(log) { return "Teh. napaka kat. 1"; },
          text: function(log) {
            return scoresheet.data.logs.withMinute(log.minute, log.quarter, scoresheet.playerNameWithNumber(log.pid) + " je prejel tehnično napako kategorije 1.");
          }
        },
        changeTech2Fouls: {
          name: function(log) { return "Teh. napaka kat. 2"; },
          text: function(log) {
            return scoresheet.data.logs.withMinute(log.minute, log.quarter, scoresheet.playerNameWithNumber(log.pid) + " je prejel tehnično napako kategorije 2.");
          }
        },
        changeEjections: {
          name: function(log) { return "Izključitev"; },
          text: function(log) {
            return scoresheet.data.logs.withMinute(log.minute, log.quarter, scoresheet.playerNameWithNumber(log.pid) + " je bil izključen iz tekme.");
          }
        },
        changeFights: {
          name: function(log) { return "Pretep"; },
          text: function(log) {
            return scoresheet.data.logs.withMinute(log.minute, log.quarter, scoresheet.playerNameWithNumber(log.pid) + " je bil v udeležen v pretep.");
          }
        },
        changeScore: {
          // Number(), not ===: a fresh entry carries diff as a number, but the
          // saved document carries it as a string ("2") — everything in these
          // JSON columns arrives back as a string through params — so after a
          // reload the strict test failed for every free throw and the log table
          // read "Koš za 1" instead of "Zadet prosti met". The filter dropdown is
          // built with numbers (buildLogFilterOptions), so those rows could not
          // be filtered at all. Found by the vocabulary-parity test in
          // test/system/follow_play_by_play_test.rb.
          name: function(log) {
            if(Number(log.diff) === 0) {
              return "Zgrešen prosti met";
            } else if (Number(log.diff) === 1) {
              return "Zadet prosti met";
            } else {
              return "Koš za " + log.diff;
            }
          },
          text: function(log) {
            if(Number(log.diff) === 0) {
              return scoresheet.data.logs.withMinute(log.minute, log.quarter, scoresheet.playerNameWithNumber(log.pid) + " je zgrešil prosti met.");
            } else if (Number(log.diff) === 1) {
              return scoresheet.data.logs.withMinute(log.minute, log.quarter, scoresheet.playerNameWithNumber(log.pid) + " je zadel prosti met.");
            } else {
              return scoresheet.data.logs.withMinute(log.minute, log.quarter, scoresheet.playerNameWithNumber(log.pid) + " je zadel za "+log.diff+".");
            }
          }
        },
        cancelFt: {
          name: function(log) { return "Preklican prosti met"; },
          text: function(log) {
            return scoresheet.data.logs.withMinute(log.minute, log.quarter, "Igralcu " + scoresheet.playerNameWithNumber(log.pid) + " je bil preklican prosti met.");
          }
        },
        changeTimeouts: {
          name: function(log) { return "Minuta odmora"; },
          text: function(log) {
            var team = log.type == 'home' ? "Domača" : "Gostujoča";
            return scoresheet.data.logs.withMinute(log.minute, log.quarter, team + " ekipa je vzela minuto odmora.");
          }
        },
        changeConfirmTeamSelection: {
          name: function(log) { return "Potrjeni ekipi C in D"; },
          text: function(log) {
            var team = log.type == 'home' ? "domačo" : "gostujočo";
            return `Potrjeni ekipi C in D za ${team} ekipo.`;
          }
        },
        removeTimeout: {
          name: function(log) { return "Odvzeta minuta odmora"; },
          text: function(log) {
            var team = log.type == 'home' ? "Domači" : "Gostujoči";
            return scoresheet.data.logs.withMinute(log.minute, log.quarter, team + " ekipi je bila odvzeta minuta odmora.");
          }
        },
        changeQuarter: {
          name: function(log) { return "Konec četrtine"; },
          text: function(log) {
            var matchFinishedText = log.matchFinished ? " Konec tekme." : "";
            if(scoresheet.isOT(log.quarter)) {
              return "Zaključen " + scoresheet.quarterName(log.quarter) + "." + matchFinishedText;
            } else {
              return "Zaključena " + scoresheet.quarterName(log.quarter) + "." + matchFinishedText;
            }
          }
        },
        changeChallenge: {
          name: function(log) { return "HCC"; },
          text: function(log) {
            var team = log.type == 'home' ? "Domača" : "Gostujoča";
            return scoresheet.data.logs.withMinute(log.minute, log.quarter, team + " ekipa je izkoristila trenerski izziv (HCC).");
          }
        },
        finishMatch: {
          name: function(log) { return "Konec tekme"; },
          text: function(log) {
            return "Konec tekme."
          }
        },
        changeBenchTFouls: {
          // called with no log when the filter and the correction dropdowns are
          // built, which is why every other name() here ignores its argument
          name: function(log) { return log && log.mark === 'BD' ? "Izključitev — pripis" : "Teh. napaka"; },
          text: function(log) {
            // The mark always sits on the coach's line, so coachIndex names the coach
            // whoever the event was about.
            var coach = context.altBenchName(log.type, log.coachIndex);
            var txt;
            if(log.mark === 'BD') {
              txt = "Izključitev osebe s klopi se pripiše trenerju (" + coach + ").";
            } else if(log.mark === 'B') {
              txt = "Tehnično napako je prejela druga oseba na klopi. Pripiše se trenerju (" + coach + ").";
            } else {
              txt = coach + " je prejel tehnično napako.";
            }
            return scoresheet.data.logs.withMinute(log.minute, log.quarter, txt);
          }
        },
        changeBenchEjections: {
          name: function(log) { return "Izključitev"; },
          text: function(log) {
            var txt = context.altBenchName(log.type, log.coachIndex) + " je izključen.";
            return scoresheet.data.logs.withMinute(log.minute, log.quarter, txt);
          }
        },
        setFirstOffense: {
          name: function(log) { return "Prvi napad"; },
          text: function(log) {
            return "Prvi napad za " + (log.type == 'home' ? 'domačo' : 'gostujočo') + " ekipo.";
          }
        },
        changeOffense: {
          name: function(log) { return "Puščica"; },
          text: function(log) {
            return "Obrnjena puščica.";
          }
        },
        changePlayed: {
          name: function(log) { return "Vstop v igro"; },
          text: function(log) {
            return scoresheet.data.logs.withMinute(log.minute, log.quarter, scoresheet.playerNameWithNumber(log.pid) + " je prvič vstopil v igro.");
          }
        },
        changeStarting5: {
          name: function(log) { return "Začne med prvih 5"; },
          text: function(log) {
            return scoresheet.data.logs.withMinute(log.minute, log.quarter, scoresheet.playerNameWithNumber(log.pid) + " bo začel v prvi peterki.");
          }
        },
        changeCaptain: {
          name: function(log) { return "Zamenjava kapetana"; },
          text: function(log) {
            return scoresheet.data.logs.withMinute(log.minute, log.quarter, "Zamenjava " + (log.type === 'home' ? 'domačega' : 'gostujočega') + " kapetana.");
          }
        }
      }
    },
    ftType: {
      extra: {
        key: "extra",
        name: "Dodaten po zadetem košu"
      },
      technical: {
        key: "technical",
        name: "Zaradi tehnične napake"
      },
      extra2: {
        key: 'extra',
        // Same wording as the button in _ft_modal: this list is the correction
        // dropdown, and the two naming one thing differently is how a keeper
        // ends up picking the wrong one.
        name: "En met po 'throw-in' napaki"
      },
      separator1: {
        key: "separator",
        name: "────────────────────"
      },
      one_of_two: {
        key: "one_of_two",
        name: "Prvi od dveh"
      },
      two_of_two: {
        key: "two_of_two",
        name: "Drugi od dveh"
      },
      separator2: {
        key: "separator",
        name: "────────────────────"
      },
      one_of_three: {
        key: "one_of_three",
        name: "Prvi od treh"
      },
      two_of_three: {
        key: "two_of_three",
        name: "Drugi od treh"
      },
      three_of_three: {
        key: "three_of_three",
        name: "Tretji od treh"
      }
    },
    timeouts: {
      h1: 2,
      h2: 3,
      q5: 1,
      q6: 1,
      q7: 1,
      q8: 1,
      q9: 1
    },
    challengeCount: 1,
    bonus: 4,
    resetScoreEveryQuarter: false,
    fouledOut: 5,
    // fn -> the code cached in foulTypes, and the counter it bumps. Mirrors
    // Constant::FOUL_KINDS; the codes must agree with the server, which prints
    // the sheet from them.
    foulKinds: {
      changeFlagrantFouls:   { code: "FL", counter: "ufouls" },
      changeDisruptiveFouls: { code: "DI", counter: "difouls" },
      changeTech1Fouls:      { code: "T1", counter: "tfouls" },
      changeTech2Fouls:      { code: "T2", counter: "t2fouls" }
    },
    // The bench marks, and what each is worth toward the coach's disqualification —
    // two C's, or three of any C/B. Mirrors Constant::BENCH_FOUL_KINDS; the marks
    // must agree with the server, which prints the sheet from them.
    //
    // BD is the one new mark: somebody else's direct ejection charged to the coach,
    // which used to be written as a plain B and lost the fact that a person had been
    // sent off.
    benchFoulKinds: {
      C:  { counts: "C" },
      B:  { counts: "B" },
      BD: { counts: "B" },
      D:  { counts: null }
    },
    minutesPerQuarter: 10,
    numberOfQuarters: 4,
    minutesPerOT: 5,
    // maxPlayersOnCourt: 5,
    COACH: 'coach',
    OTHER: 'other',
    ASSISTANT: 'assistant',
    REPRESENTATIVE: 'representative',
    playerDefaults: function() {
      return {
        cft: 0,
        mft: 0,
        ft: 0,
        pt2: 0,
        pt3: 0,
        foulTypes: ["", "", "", "", "", "", ""],
        fouls: 0,
        ufouls: 0,
        tfouls: 0,
        // taktična and tehnična kat. 2: counted and printed, but summed by
        // nothing — isEjected adds only ufouls + tfouls
        difouls: 0,
        t2fouls: 0,
        ejections: 0,
        fights: 0,
        playedSelected: false,
        played: false,
        isStarting: false
      };
    },
    benchDefaults: function() {
      return {
        bfouls: 0,
        cfouls: 0,
        ejections: 0,
        foulTypes: ["", "", "", "", ""]
      };
    },
    teamDefaults: function(color, bench) {
      return {
        color: color,
        bench: bench,
        score: {
          q1: 0,
          q2: 0,
          q3: 0,
          q4: 0,
          q5: 0,
          q6: 0,
          q7: 0,
          q8: 0,
          q9: 0
        },
        fouls: {
          q1: 0,
          q2: 0,
          q3: 0,
          q4: 0,
          q5: 0,
          q6: 0,
          q7: 0,
          q8: 0,
          q9: 0
        },
        bonus: {
          q1: false,
          q2: false,
          q3: false,
          q4: false
        },
        timeouts: {
          h1: 0,
          h2: 0,
          q5: 0,
          q6: 0,
          q7: 0,
          q8: 0,
          q9: 0
        },
        challengeCount: 0
      }
    }
  },
  initPlayers: function() {
    for(pid in scoresheet.data.playerId2data) {
      var p = scoresheet.player(pid);
      var data = context[pid] ? context[pid] : { isCaptain: p.is_captain, number: p.number };
      context[pid] = $.extend(data, scoresheet.data.playerDefaults());  // override with defaults
    }
  },
  initBench: function() {
    var types = ['home', 'away'];
    var tmp = {
      home: [],
      away: []
    }
    for(type of types) {
      if(scoresheet.data.side2type2data[type][scoresheet.data.COACH]) {
        tmp[type].push($.extend({
          originalRole: scoresheet.data.COACH,
          firstName: scoresheet.data.side2type2data[type][scoresheet.data.COACH].firstName,
          lastName: scoresheet.data.side2type2data[type][scoresheet.data.COACH].lastName,
          // set when the captain was written as coach at prematch — the same field
          // setCoach stamps when a captain takes the bench mid-match, so both roads
          // to a player-coach end in one place
          pid: scoresheet.data.side2type2data[type][scoresheet.data.COACH].playerFmsId || null,
          role: scoresheet.data.COACH
        }, scoresheet.data.benchDefaults()));
      }

      if(scoresheet.data.side2type2data[type][scoresheet.data.ASSISTANT]) {
        tmp[type].push($.extend({
          originalRole: scoresheet.data.ASSISTANT,
          firstName: scoresheet.data.side2type2data[type][scoresheet.data.ASSISTANT].firstName,
          lastName: scoresheet.data.side2type2data[type][scoresheet.data.ASSISTANT].lastName,
          role: scoresheet.data.ASSISTANT
        }, scoresheet.data.benchDefaults()));
      } else {
        tmp[type].push($.extend({
          originalRole: scoresheet.data.ASSISTANT,
          firstName: null,
          lastName: null,
          role: null
        }, scoresheet.data.benchDefaults()));
      }
    }

    for(var i = 0; i < 17; i++) {
      for(type of types) {
        tmp[type].push($.extend({
          name: scoresheet.data.logs.other,
          originalRole: scoresheet.data.OTHER,
          role: (i === 0 ? scoresheet.data.OTHER : null)
        }, scoresheet.data.benchDefaults()));
      }
    }
    for(type of types) {
      if(scoresheet.data.side2type2data[type][scoresheet.data.REPRESENTATIVE]) {
        tmp[type].push({
          originalRole: scoresheet.data.REPRESENTATIVE,
          firstName: scoresheet.data.side2type2data[type][scoresheet.data.REPRESENTATIVE].firstName,
          lastName: scoresheet.data.side2type2data[type][scoresheet.data.REPRESENTATIVE].lastName,
          role: scoresheet.data.REPRESENTATIVE
        });
      }
    }
    return tmp;
  },
  setDefaults: function() {
    // SET PLAYERS
    scoresheet.initPlayers();

    // SET HOME AND AWAY
    var tmp = scoresheet.initBench();
    context.home = scoresheet.data.teamDefaults( context.home.color, tmp['home'] );
    context.away = scoresheet.data.teamDefaults( context.away.color, tmp['away'] );
  },
  // Twine binds against `context`, and on this screen the context does not exist
  // until the ajax below answers — initAfterLoad builds it whole, functions and data
  // together, out of playerId2data / side2type2data / the bench. The layout used to
  // bind immediately anyway, so every binding it met named a function that was not
  // there yet and threw `ReferenceError: <name> is not defined` into the console.
  //
  // Harmless in production (the next refreshContext repaints everything) but real
  // noise, and it made system tests fail by timing — the test helper had to filter
  // the message out to tell a genuine error from this one.
  //
  // So this screen takes the binding over. The flag is set synchronously, before the
  // layout's own $(function(){...}) runs — page_javascript is yielded first, and
  // jQuery keeps ready handlers in registration order. bindTwine then runs on BOTH
  // answers: the error path paints its message through the same bindings.
  //
  // The markup's default state already matches: the spinner has no .hide and the
  // sheet does, so an unbound page is a loading page.
  init: function(saveUrl, timeUrl, inviteUrl, signatureUrl, reportInviteUrl, matchId) {
    window.twineBindDeferred = true;
    scoresheet.data.saveUrl = saveUrl;
    scoresheet.data.timeUrl = timeUrl;
    scoresheet.data.inviteUrl = inviteUrl;
    scoresheet.data.signatureUrl = signatureUrl;
    scoresheet.data.reportInviteUrl = reportInviteUrl;
    scoresheet.data.matchId = matchId;
    // archive housekeeping (versions older than 14 days), and a best-effort
    // plea to the browser not to evict this origin's storage under disk pressure
    scoresheet.cleanArchive();
    try { navigator.storage && navigator.storage.persist && navigator.storage.persist(); } catch(e) {}
        
    $.ajax(scoresheet.data.saveUrl, {
      success: function(data) {
        // take scoresheetobj from localstorage if later saved then on server!
        let obj = scoresheet.getFromLocalStorage();
        let scoresheetObj = data.scoresheetObj;
        if(data.scoresheetObj.serverSaveTime && obj.scoresheetObj.localStorageSaveTime && data.scoresheetObj.serverSaveTime < obj.scoresheetObj.localStorageSaveTime
           && !scoresheet.cacheIsAnotherMatch(obj, data)) {
          scoresheetObj = obj.scoresheetObj;
        }
        // stamp the identity so the NEXT load can check it
        scoresheet.data.fmsId = data.fmsId;

        scoresheet.initAfterLoad(data.playerId2data, scoresheetObj, data.side2type2data, data.rules);
        setContext("loaded", true);
        setContext("isError", false);
        refreshContext();
        // A team can be captainless across a reload — the ejected captain keeps
        // isCaptain, which is what makes the team captainless in the first place —
        // and the dialog only ever appeared on the NEXT attempted entry. It is
        // raised on load as well now, so refreshing the page cannot get past it.
        // Not after the final whistle: nothing more is entered then, and an
        // inescapable dialog would block the signatures and the report.
        context.demandCaptains();
        scoresheet.bindTwine();
      },
      error: function() {
        setContext("isError", true);
        scoresheet.bindTwine();
      }
    });

  },
  // Idempotent on purpose: the two ajax outcomes are exclusive, but a retry or a
  // second init must not re-bind the same nodes twice.
  bindTwine: function() {
    if(scoresheet.data.twineBound) return;

    scoresheet.data.twineBound = true;
    Twine.reset(context).bind().refresh();
  },
  initAfterLoad: function(playerId2data, scoresheetObj, side2type2data, rules) {
    scoresheet.data.actionStates = [scoresheet.data.state.FT_PRESSED, scoresheet.data.state.PT2_PRESSED, scoresheet.data.state.PT3_PRESSED, scoresheet.data.state.FOUL_PRESSED, scoresheet.data.state.FTFOUL_PRESSED, scoresheet.data.state.UFOUL_PRESSED, scoresheet.data.state.TFOUL_PRESSED, scoresheet.data.state.FLAGRANT_PRESSED, scoresheet.data.state.DISRUPTIVE_PRESSED, scoresheet.data.state.TECH1_PRESSED, scoresheet.data.state.TECH2_PRESSED, scoresheet.data.state.EJECTION_PRESSED, scoresheet.data.state.FIGHT_PRESSED, scoresheet.data.state.TIMEOUT_TAKEN, scoresheet.data.state.REMOVE_TIMEOUT]
    scoresheet.data.entityStates = [scoresheet.data.state.PLAYER_SELECTED, scoresheet.data.state.COACH_SELECTED, scoresheet.data.state.ASSISTANT_SELECTED, scoresheet.data.state.OTHER_SELECTED]
    scoresheet.data.playerId2data = playerId2data;
    scoresheet.data.side2type2data = side2type2data;    // { home: { coach: {...}, assistant: {...} }, away: { coach: {...}, assistant: {...} } }

    if(rules.bonus) {
      scoresheet.data.bonus = rules.bonus;
    }
    scoresheet.data.resetScoreEveryQuarter = rules.reset_score_every_quarter;

    // INIT PLAYERS
    scoresheet.initPlayers();

    // INIT HOME AND AWAY BENCH
    var tmp = scoresheet.initBench();

    context = $.extend(context, {
      ft: {},
      showPlayerNames: true,
      fullScreen: false,
      logId: 0,
      saving: false,
      saveOnSuccess: false,
      left: 'home',
      right: 'away',
      playerId: null,               // for player_selected
      ttype: null,                   // for coach_selected and ASSISTANT_SELECTED
      selectedBenchIndex: null,               // when ttype != null, this gives us who is selected
      previousState: null,
      previousPreviousState: null,
      state: null,
      quarter: 1,
      minute: 1,
      matchFinished: false,
      firstOffense: null,
      nextOffense: null,
      waitingForMinute: false,
      home: scoresheet.data.teamDefaults( side2type2data.home.color || { bg: '#007bff', text: '#ffffff' }, tmp['home'] ),
      away: scoresheet.data.teamDefaults( side2type2data.away.color || { bg: '#dc3545', text: '#ffffff' }, tmp['away'] ),
      referees: [],
      logs: [],
      logTable: {
        limit: 10,
        page: 1,
        // A separate flag rather than a seventh entry in `filters`: doFiltersApply
        // compares filter values to log values with `fval != lval`, which a boolean
        // would not survive.
        onlyFlagged: false,
        filters: {
          quarter: null,
          minute: null,
          state: null,
          type: null,
          number: null,
          entity: null,          // igralec, trener, pomočnik, nekdo od ostalih
          numberEntity: null
        }
      },
      edit: {},
      bench: function(type, i) {
        return this[type].bench[i];
      },
      // The three loops below were written `for(var i = 0; this[type].bench.length; i++)`
      // — no `i <`, so the condition is the length and never false. They terminated
      // only because a coach was always found before running off the end; with the
      // bench now reached through one button, benchIndex is asked for roles that can
      // legitimately be absent, and an absent one hung the page.
      benchIndex: function(type, role) {
        for(var i = 0; i < this[type].bench.length; i++) {
          var b = this.bench(type, i);
          if(b && b.role == role) return i;
        }
        return null;
      },
      nextEligibleBenchIndex: function(type, start) {
        for(var i = start; i < this[type].bench.length; i++) {
          var b = this.bench(type, i);
          if(!this.isBenchEjected(type, i) && !(b.originalRole == scoresheet.data.ASSISTANT && b.role == null)) return i;
        }
        return null;
      },
      coach: function(type) {
        var i = this.coachIndex(type);
        return i === null ? null : this.bench(type, i);
      },
      coachIndex: function(type) {
        for(var i = 0; i < this[type].bench.length; i++) {
          if(this.isCoach(type, i)) return i;
        }
        return null;
      },
      // Which bench row IS this player, if any — the player-coach link, read from
      // the side the player is on. null when he only plays, which is nearly always.
      benchIndexForPlayer: function(type, pid) {
        var bench = this[type].bench || [];
        for(var i = 0; i < bench.length; i++) {
          if(bench[i] && bench[i].pid && String(bench[i].pid) === String(pid)) return i;
        }
        return null;
      },
      isPlayerBenchEjected: function(pid) {
        // check if player was ejected as a coach, then he must leave the hall, and thus cannot participate in the game in any role
        var type = this.type(pid);
        for(var i = 0; i < this[type].bench.length; i++) {
          if(this.bench(type, i).pid == pid && this.isBenchEjected(type, i)) return true;
        }
        return false;
      },
      // A player who is also the coach keeps TWO accounts on the sheet — his own
      // fouls, and the marks against him as coach — and the rules eject him on
      // combinations that mix them. His player fouls are therefore added to both
      // sums below. Without a link this returns 0 and the rule is exactly what it
      // was, which is every match that has no player-coach.
      benchPlayerFouls: function(type, i) {
        var b = this.bench(type, i);
        if(!b || !b.pid) return 0;

        var p = this[b.pid];
        if(!p) return 0;
        return (p.ufouls || 0) + (p.tfouls || 0);
      },
      isBenchEjected: function(type, i) {
        var b = this.bench(type, i);
        // the representative is pushed onto the bench without benchDefaults, so it
        // has no counters at all — it is on the bench, not on the sheet
        if(!b || b.cfouls === undefined) return false;
        var own = this.benchPlayerFouls(type, i);
        return b.ejections >= 1 || (b.cfouls + own) >= 2 || (b.cfouls + b.bfouls + own >= 3);
      },
      setBenchRole: function(bench, role) {
        bench.previousRole = bench.role;
        bench.role = role;
      },
      setCoach: function(bench, isOther, type) {
        this.setBenchRole(bench, scoresheet.data.COACH);
        if(isOther) {
          // must set captain, or if captain was already ejected as a coach, then another player must be selected
          var pid = this.currentCaptain(type);
          if(pid) {
            var pdata = scoresheet.player(pid);
            bench.firstName = pdata.first_name;
            bench.lastName = pdata.last_name;
            bench.pid = pid;
          } else {
            // should never get here??? 
          }
        }
      },
      setAssistant: function(bench) { 
        this.setBenchRole(bench, scoresheet.data.ASSISTANT);
      },
      setOther: function(bench) {
        this.setBenchRole(bench, scoresheet.data.OTHER);
      },
      setRoleNull: function(bench) {
        this.setBenchRole(bench, null);
      },
      setAssistantAndOtherRoles: function(bench1, bench2) {
        if(this.isOriginallyAssistant(bench1)) {
          this.setAssistant(bench1);
          this.setOther(bench2);
        } else {
          this.setOther(bench1);
          this.setRoleNull(bench2);
        }
      },
      benchName: function(type, i, lastNameFirst, withInitials) {
        var b = this.bench(type, i);
        if(!this.isOther(b) && b.firstName && b.lastName) {
          return scoresheet.name(b.firstName, b.lastName, lastNameFirst, withInitials);
        } else {
          return b.name;
        }
      },
      altBenchName: function(type, i, lastNameFirst, withInitials) {
        var name = this.benchName(type, i, lastNameFirst, withInitials);
        return name === scoresheet.data.logs.other ? "Nekdo od ostalih" : name;
      },
      benchFouls: function(type, i) {
        var b = this.bench(type, i);
        return b.bfouls + b.cfouls + b.ejections;
      },
      // Which counter a mark bumps. `cfouls` and `bfouls` are the disqualification
      // rule's two buckets, and the mark says which one it lands in — CT with C, BT
      // and BD with B. It used to be mark.toLowerCase() + "fouls", which worked only
      // while every mark was a single letter.
      benchCounterFor: function(mark) {
        var counts = (scoresheet.data.benchFoulKinds[mark] || {}).counts;
        return counts ? counts.toLowerCase() + "fouls" : null;
      },

      // --- the bench as ONE thing on screen -----------------------------------
      //
      // The bench is one line on the paper sheet and one button here. Everything
      // below reads the CURRENT coach, so an ejection that promotes somebody moves
      // the button to them, with their own three empty dots.
      hasCoach: function(type) {
        return this.coachIndex(type) !== null;
      },
      coachName: function(type) {
        var i = this.coachIndex(type);
        return i === null ? "Trener ni vpisan" : this.benchName(type, i, true, true);
      },
      // Who is on the bench right now, by the role they hold NOW: "T NOVAK G. · P
      // KOS M.". The P half simply disappears when there is no assistant — and when
      // the coach is sent off the assistant becomes the T, which leaves none.
      benchOfficialsLabel: function(type) {
        var con = this;
        var parts = [[scoresheet.data.COACH, 'T'], [scoresheet.data.ASSISTANT, 'P']]
          .map(function(pair) {
            var i = con.benchIndex(type, pair[0]);
            return i === null ? null : pair[1] + " " + con.benchName(type, i, true, true);
          })
          .filter(Boolean);
        return parts.join(" · ");
      },
      isBenchGroupSelected: function(type) {
        return this.ttype === type && this.selectedBenchIndex !== null;
      },
      isCoachFoulCommited: function(type, j) {
        var i = this.coachIndex(type);
        return i !== null && this.isBenchFoulCommited(type, i, j);
      },
      hasCoachFoulMark: function(type, j) {
        var i = this.coachIndex(type);
        return i !== null && this.hasBenchFoulMark(type, i, j);
      },
      coachFoulMark: function(type, j) {
        var i = this.coachIndex(type);
        return i === null ? "" : this.benchFoulMark(type, i, j);
      },
      // Who has been sent off, kept on screen for the rest of the match. Without the
      // per-person cards this is the only place it can still be read.
      ejectedBench: function(type) {
        var out = [];
        for(var i = 0; i < this[type].bench.length; i++) {
          if(this.isBenchEjected(type, i)) out.push(i);
        }
        return out;
      },
      hasEjectedBench: function(type) {
        return this.ejectedBench(type).length > 0;
      },
      ejectedBenchLine: function(type) {
        var con = this;
        var names = this.ejectedBench(type).map(function(i) {
          var name = con.benchName(type, i, true, true);
          if(name === scoresheet.data.logs.other) return "nekdo s klopi";
          return con.benchRoleWord(type, i) + " " + name;
        });
        return names.length ? "Izključeni: " + names.join(" · ") : "";
      },
      // What a person WAS, not what they are now: an ejected coach has no role left.
      // A slot carrying a pid is the captain, stamped in when he took the bench.
      benchRoleWord: function(type, i) {
        var b = this.bench(type, i);
        if(b.originalRole === scoresheet.data.COACH) return scoresheet.data.logs.coach;
        if(b.originalRole === scoresheet.data.ASSISTANT) return scoresheet.data.logs.assistant;
        if(b.pid) return "Kapetan";
        return "S klopi";
      },
      isCaptain: function(pid, type) {
        return pid == this.currentCaptain(type);
      },
      youngestTeamLabel: function(pid) {
        var arr = [];
        var p1 = scoresheet.player(pid);
        var p2 = this[pid];
        if(this.quarter <= 2) {
          if( (p1 && p1.in_a) || (p2 && p2.in_a) ) {
            arr.push('A');
          }
          if( (p1 && p1.in_b) || (p2 && p2.in_b) ) {
            arr.push('B');
          }
        } else {
          if( (p1 && p1.in_c) || (p2 && p2.in_c) ) {
            arr.push('C');
          }
          if( (p1 && p1.in_d) || (p2 && p2.in_d) ) {
            arr.push('D');
          }
        }
        return arr.join(",");
      },
      playerName: function(pid, lastNameFirst, withInitials) {
        return scoresheet.playerName(pid, lastNameFirst, withInitials);
      },
      isWithoutCaptain: function(type) {
        return this.currentCaptain(type) == null;
      },
      currentCaptain: function(type) {
        for(pid in scoresheet.data.playerId2data) {
          var pdata = scoresheet.player(pid);
          var p = this[pid];
          if(pdata.type == type && p.isCaptain && !this.isEjected(pid)) {
            return pid;
          }
        }
        return null;
      },
      typeName: function(type) {
        return "("+ scoresheet.data.logs[type] +")";
      },
      realScore: function(type) {
        var total = 0;
        for(key in this[type].score) {
          total += this[type].score[key];
        }
        return total;
      },
      finalYoungestScore: function(type) {
        var otherType = this.otherType(type);

        var finalYoungestScoreType = 0;
        var finalYoungestScoreOtherType = 0;

        for(var i = 1; i <= 4; i++) {
          finalYoungestScoreType += this.quarterYoungestScore(type, i);
          finalYoungestScoreOtherType += this.quarterYoungestScore(otherType, i);
        }

        if(finalYoungestScoreType == finalYoungestScoreOtherType) {
          var finalRealScoreType = this.realScore(type);
          var finalRealScoreOtherType = this.realScore(otherType);

          if(finalRealScoreType > finalRealScoreOtherType) {
            return finalYoungestScoreType+1;
          } else {
            return finalYoungestScoreType;
          }
        } else {
          return finalYoungestScoreType;
        }
      },
      quarterYoungestScore: function(type, q) {
        var otherType = this.otherType(type);

        var realScoreType = this[type].score["q"+q];
        var realScoreOtherType = this[otherType].score["q"+q];

        if(realScoreType > realScoreOtherType) {
          return 3;
        } else if(realScoreType == realScoreOtherType) {
          return 2;
        } else {
          return 1;
        }
      },
      score: function(type) {
        if(scoresheet.data.resetScoreEveryQuarter) {
          if(this.matchFinished || this.quarter > 4) {
            return this.finalYoungestScore(type);
          } else {
            return this[type].score["q"+this.quarter];
          }
        } else {          
          return this.realScore(type);
        }
      },
      scoreTextByQuarters: function() {
        var ltype = this.left;
        var rtype = this.right;
        var scores = [];
        var n = this.quarter - (this.matchFinished ? 1 : 0);
        for(var i = 1; i <= n; i++) {
          if(i < this.quarter && scoresheet.data.resetScoreEveryQuarter) {
            scores.push( this.quarterYoungestScore(ltype, i) + " - " + this.quarterYoungestScore(rtype, i) );
          } else {
            scores.push( this[ltype].score["q"+i] + " - " + this[rtype].score["q"+i] );
          }
        }
        return scores.join(" ; ");
      },
      quarterName: function() {
        if(this.matchFinished) {
          return "Konec tekme";
        } else {
          return scoresheet.quarterName(this.quarter);
        }
      },
      // Read-only getters for the quarter rail and the per-quarter panel — the same
      // facts scoreTextByQuarters flattened into one string, one quarter at a time.
      quarterRowShown: function(q) {
        if(this.matchFinished) return q <= scoresheet.data.numberOfQuarters || q <= Number(this.lastPlayedQuarter);
        return q <= scoresheet.data.numberOfQuarters || q <= this.quarter;
      },
      isQuarterCurrent: function(q) {
        return !this.matchFinished && q == this.quarter;
      },
      isQuarterPlayed: function(q) {
        // ending a quarter bumps the counter before the finish dialog, so after a
        // finish the counter names a quarter that never saw play — the recorded
        // lastPlayedQuarter (a string when restored from JSON) is the truth then
        if(this.matchFinished) return q <= Number(this.lastPlayedQuarter);
        return q < this.quarter;
      },
      quarterRowLabel: function(q) {
        if(q <= scoresheet.data.numberOfQuarters) return q + "/" + scoresheet.data.numberOfQuarters;
        return "P" + (q - scoresheet.data.numberOfQuarters);
      },
      quarterScoreText: function(q) {
        var played = this.quarter - (this.matchFinished ? 1 : 0);
        if(q > played && !(q == this.quarter && !this.matchFinished)) return "—";
        var l = this.left, r = this.right;
        if(q < this.quarter && scoresheet.data.resetScoreEveryQuarter) {
          return this.quarterYoungestScore(l, q) + " – " + this.quarterYoungestScore(r, q);
        }
        return this[l].score["q"+q] + " – " + this[r].score["q"+q];
      },
      // The score of the MATCH as it stands, named by team so neither number can be
      // read against the wrong side. score() is the same answer the scoreboard gives:
      // the running total, or — where the score resets every quarter — that quarter's,
      // which is what the score is there.
      //
      // Was finalScoreText, which is where it is asked from twice; but the quarter
      // confirm asks it too, and nothing about it is final.
      currentScoreText: function() {
        return this.complaint[this.left].teamName + " <b>" + this.score(this.left) + " : " + this.score(this.right) + "</b> " + this.complaint[this.right].teamName;
      },
      // The finish button shows only at a quarter boundary: past the regular count
      // AND before anything lands in the running quarter. That is exactly when
      // finishMatch's bookkeeping (the end stamped at quarter-1) is right — one
      // event later the quarter has been played and the proper exit is ending it.
      // Corrections don't count as play, same as the server's replay (ADR-0001).
      mayFinishMatch: function() {
        if(this.matchFinished || !this.isFirstOffenseSet()) return false;
        if(this.quarter <= scoresheet.data.numberOfQuarters) return false;
        for(log of this.logs) {
          if(log.editedById) continue;
          if(log.quarter == this.quarter) return false;
        }
        return true;
      },
      // The bar's Konec tekme used to call finishMatch() directly — a terminal action
      // with no confirmation. It asks the same question the overtime flow asks.
      finishMatchAsk: function() {
        alerts.confirmation({
          type: 'info',
          title: "Je tekma končana?",
          body: "<p>Rezultat: " + this.currentScoreText() + ".</p>",
          yesLabel: "DA, KONEC TEKME",
          noLabel: "NE, ŠE NE",
          yesCallback: this.finishMatch.bind(this)
        });
      },
      // "Zaključi 3. četrtino" — the confirm is the last defence against ending the
      // wrong one, so the button names the one it ends. Accusative by replacement,
      // since "podaljšek" is the same in both cases.
      endQuarterLabel: function() {
        return "Zaključi " + scoresheet.quarterName(this.quarter).replace("četrtina", "četrtino");
      },
      // The band names the last LINE of the log — the same line "Odstrani zadnji
      // vpis" below it will take out. If it named the last line that still counts
      // instead, the two would disagree the moment an entry was crossed out, and
      // the keeper would read one entry and remove another. A crossed-out line is
      // shown as crossed out (isLastLogStruckOut) rather than hidden.
      lastLogText: function() {
        var log = this.reversedLog(0);
        return log ? this.logActionText(log) : "/";
      },
      isActionState: function(state) {
        return scoresheet.data.actionStates.indexOf( state ) >= 0;
      },
      isEntityState: function(state) {
        return scoresheet.data.entityStates.indexOf( state ) >= 0;
      },
      entityStateText: function(state, withExtra) {
        var txt = "";
        if(state === scoresheet.data.state.PLAYER_SELECTED) {
          txt = scoresheet.playerNameWithNumber(this.playerId);
        } else if(state === scoresheet.data.state.COACH_SELECTED) {
          txt = "Trener";
        } else if(state === scoresheet.data.state.ASSISTANT_SELECTED) {
          txt = "Pomočnik";
        } else if(state === scoresheet.data.state.OTHER_SELECTED) {
          txt = "Nekdo od ostalih";
        } else {
          return "[NO ENTITY]";
        }
        return txt + (withExtra ? " | " + scoresheet.data.stateNameExtra[this.state] : "");
      },
      actionStateText: function(state, withExtra) {
        return scoresheet.data.stateName[state] + (withExtra ? " | " + scoresheet.data.stateNameExtra[this.state] : "");
      },
      // What is being composed right now. The "Izberi minuto ali potrdi minuto s
      // preslednico" instruction used to be tacked onto the end of this string, so
      // the state and the advice were one run of text that only appeared once the
      // entry was already half-made. It is a permanent hint beside the minute strip
      // now, which leaves this short enough to set large — it is the thing the old
      // UI answered worst, at 0.875rem inside an alert-sm.
      currentStateText: function() {
        if(this.state === null) {
          return "Nič — izberi dogodek in igralca";
        } else if(this.isActionState(this.state) && this.isEntityState(this.previousState)) {
          return this.entityStateText(this.previousState) + " · " + this.actionStateText(this.state);
        } else if(this.isActionState(this.previousState) && this.isEntityState(this.state)) {
          return this.entityStateText(this.state) + " · " + this.actionStateText(this.previousState);
        } else if(this.isEntityState(this.state)) {
          return this.entityStateText(this.state, true);
        } else if(this.isActionState(this.state) || this.state === scoresheet.data.state.CHANGE_CAPTAIN) {
          return this.actionStateText(this.state, true);
        }
      },
      isPlayerInC: function(pid) {
        var p = this[pid];
        return p && p.in_c;
      },
      onPlayerInCChange: function(el, pid) {
        var p = this[pid];
        if(p) {
          p.in_c = el.checked;
          scoresheet.player(pid).in_c = el.checked;
          refreshContext();
          scoresheet.save();
        }
      },
      isPlayerInD: function(pid) {
        var p = this[pid];
        return p && p.in_d;
      },
      onPlayerInDChange: function(el, pid) {
        var p = this[pid];
        if(p) {
          p.in_d = el.checked;
          scoresheet.player(pid).in_d = el.checked;
          refreshContext();
          scoresheet.save();
        }
      },
      isFoulCommited: function(pid, i) {
        return this[pid].fouls >= i;
      },
      isCoach: function(type, i) {
        return this.bench(type, i).role === scoresheet.data.COACH;
      },
      isPreviousCoach: function(bench) {
        return bench.previousRole === scoresheet.data.COACH;
      },
      isAssistant: function(bench) {
        return bench.role === scoresheet.data.ASSISTANT;
      },
      isPreviousAssistant: function(bench) {
        return bench.previousRole === scoresheet.data.ASSISTANT;
      },
      isBenchAssistant: function(type, i) {
        return this.isAssistant( this.bench(type, i) );
      },
      isOriginallyAssistant: function(bench) {
        return bench.originalRole === scoresheet.data.ASSISTANT;
      },
      isOriginallyOther: function(bench) {
        return bench.originalRole === scoresheet.data.OTHER;
      },
      isOther: function(bench) {
        return bench.role === scoresheet.data.OTHER;
      },
      isPreviousOther: function(bench) {
        return bench.previousRole === scoresheet.data.OTHER;
      },
      isBenchOther: function(type, i) {
        return this.isOther( this.bench(type,i) );
      },
      isBenchFoulCommited: function(type, benchIndex, foulIndex) {
        return this.benchFouls(type, benchIndex) > foulIndex;
      },
      hasBenchFoulMark: function(type, benchIndex, foulIndex) {
        return this.benchFoulMark(type, benchIndex, foulIndex) && this.isBenchFoulCommited(type, benchIndex, foulIndex);
      },
      benchFoulMark: function(type, benchIndex, foulIndex) {
        return this.bench(type, benchIndex).foulTypes && this.bench(type, benchIndex).foulTypes[foulIndex];
      },
      isFoulCancelled: function(pid, i) { // i =1,2,3,4,5
        if(this[pid].fouls < i || ["", "|"].indexOf( this[pid].foulTypes[i-1] ) >= 0) return false; 

        var n = 0;
        for(log of this.logs) {
          if(log.pid == pid && this.isFoul(log)) {
            n++;
          }
          if(n == i) {
            return !!log.cancelledWithId;
          }
        }
        return false;
      },
      isBenchFoulCancelled: function(pid, i) {
        return this.bench(pid, i)
      },
      hasFoulMark: function(pid, i) {
        return this.foulMark(pid, i) && this.isFoulCommited(pid, i);
      },
      foulMark: function(pid, i) {
        return this[pid].foulTypes[i-1];
      },
      teamFoul: function(type, i) {
        return this.foulsInCurrentQuarter(type) >= i ? "X" : "";
      },
      foulsInCurrentQuarter: function(type) {
        return this[type].fouls["q"+this.quarter] + ( this.isOT() ? this[type].fouls.q4 : 0 );
      },
      isBonusInCurrentQuarter: function(type) {
        var qkey = this.isOT() ? 'q4' : 'q' + this.quarter;
        return this[type].bonus && this[type].bonus[qkey];
      },
      isFouledOut: function(pid) {
        var p = this[pid];
        return p.fouls >= scoresheet.data.fouledOut;
      },
      isEjected: function(pid) {
        var p = this[pid];
        return p && ( p.ejections > 0 || p.fights > 0 || p.ufouls + p.tfouls >= 2 || this.isPlayerBenchEjected(pid) );
      },
      // The three slots of the entry line: the same facts currentStateText joins into
      // one sentence, one slot at a time, so an empty slot can say what it waits for.
      slotActionText: function() {
        if(this.state !== null && this.isActionState(this.state)) return scoresheet.data.stateName[this.state];
        if(this.previousState !== null && this.isActionState(this.previousState) && this.isEntityState(this.state)) return scoresheet.data.stateName[this.previousState];
        if(this.state === scoresheet.data.state.CHANGE_CAPTAIN) return scoresheet.data.stateName[this.state];
        if(this.state === scoresheet.data.state.CHALLENGE_TAKEN) return scoresheet.data.stateName[this.state];
        // verb-first order: the machine keeps the pending commit as an opaque closure,
        // so the verb's name lives in the annotation selectPlayer/selectBench wrote
        if(this.waitingForMinute && this.pendingActionState) return scoresheet.data.stateName[this.pendingActionState];
        return "";
      },
      slotEntityText: function() {
        if(this.state !== null && this.isEntityState(this.state)) return this.entityStateText(this.state);
        if(this.previousState !== null && this.isEntityState(this.previousState) && this.isActionState(this.state)) return this.entityStateText(this.previousState);
        // a team-scoped entry (timeout, HCC): the slot names the team — the grey
        // "Izberi igralca" prompt claimed a player was still needed when only the
        // minute was. Display only, same shape as pendingActionState.
        if(this.state !== null && this.pendingTeamType && this.complaint[this.pendingTeamType]) return this.complaint[this.pendingTeamType].teamName;
        return "";
      },
      logsCount: function() { return (this.logs || []).length; },
      hasSlotAction: function() { return this.slotActionText() !== ""; },
      hasSlotEntity: function() { return this.slotEntityText() !== ""; },
      isActionSelected: function(state) {
        return this.state === state || (this.state !== null && this.previousState === state && !this.isActionState(this.state));
      },
      isPlayerSelected: function(pid) {
        return this.playerId === pid;
      },
      isDisabled: function(pid) {
        return this.isEjected(pid);   // || this.isFouledOut(pid)
      },
      isLastLogHome: function() {
        return this.isLogOfType( this.reversedLog(0), 'home');
      },
      isLastLogAway: function() {
        return this.isLogOfType( this.reversedLog(0), 'away');
      },
      isLogOfType: function(log, type) {
        return log && log.type === type;
      },
      isArrayLogHome: function(i) {
        return this.isLogOfType( this.rowLog(i), 'home');
      },
      isArrayLogAway: function(i) {
        return this.isLogOfType( this.rowLog(i), 'away');
      },
      hasPlayed: function(pid) {
        return this[pid].played;
      },
      // --- eligibility after log-table corrections (additive; the state machine
      // is untouched). Uredi/Vrini/Izbriši rebuild every counter from the logs, so
      // a corrected entry CAN disqualify a player — but only the interactive path
      // ran the validators that own the popups: a keeper who moved a second
      // technical onto #4 saw the tile grey out and nothing scream. The mutation
      // sites snapshot eligibility before the rebuild and announce what BECAME
      // true after it, through the SAME validators (same dialogs) the live path
      // uses. Postmortem corrections (finished match) stay silent on purpose.
      eligibilitySnapshot: function() {
        var snap = { players: {}, bench: {}, captainless: {} };
        for(pid in scoresheet.data.playerId2data) {
          snap.players[pid] = { ejected: this.isEjected(pid), fouledOut: this.isFouledOut(pid) };
        }
        for(type of ['home', 'away']) {
          snap.captainless[type] = this.isWithoutCaptain(type);
          var bench = this[type].bench || [];
          for(var i = 0; i < bench.length; i++) {
            if(bench[i]) snap.bench[type + i] = this.isBenchEjected(type, i);
          }
        }
        return snap;
      },
      announceEligibilityChanges: function(before) {
        if(this.matchFinished) return;
        for(pid in scoresheet.data.playerId2data) {
          var was = before.players[pid] || {};
          if(!was.ejected && this.isEjected(pid)) {
            this.validateEligible(pid);
          } else if(!was.fouledOut && this.isFouledOut(pid)) {
            this.validateFouledOut(pid);
          }
        }
        for(type of ['home', 'away']) {
          var bench = this[type].bench || [];
          for(var i = 0; i < bench.length; i++) {
            if(bench[i] && !before.bench[type + i] && this.isBenchEjected(type, i)) {
              this.validateBenchEligible(type, i);
            }
          }
          if(!before.captainless[type] && this.isWithoutCaptain(type)) {
            this.validateWithoutCaptain(type);
          }
        }
      },
      checkEligibleToContinue: function(type, pid) {
        if(!this.validateEligible(pid, true) || !this.validateFouledOut(pid)) {
          this.reset();
        }
      },
      // After an entry, not before it: says the person is out, and — when the person
      // was the coach — who takes the bench. One dialog rather than two, because both
      // facts belong to the same moment.
      checkBenchEligibleToContinue: function(type, index, newCoachIndex) {
        if(!this.isBenchEjected(type, index)) return true;

        // A player-coach is named as both, because that is why he is out: neither
        // account on its own would have reached the limit.
        var who = this.altBenchName(type, index);
        var isPlayerCoach = !!(this.bench(type, index) || {}).pid;
        var html = "<p class='crit__who'>" + (isPlayerCoach ? "Igralec/trener " : "") + who + "</p>" +
                   "<p class='crit__what'>(" + this.complaint[type].teamName + ") je izključen iz tekme.</p>" +
                   "<p class='crit__rule'>Oseba mora zapustiti klop.</p>";
        if(isPlayerCoach) {
          html += "<p class='crit__rule'>Ne sme več igrati niti voditi ekipe.</p>";
        }
        if(newCoachIndex !== null && newCoachIndex !== undefined) {
          html += "<p class='crit__rule'>" + this.takeoverText(type, newCoachIndex) + "</p>";
        }
        alerts.warning(html, { title: "Izključitev", continueLabel: "RAZUMEM", modalClass: "dialog--crit" });
        this.reset();
        return false;
      },
      // Who leads the team now. The rule is already in nextEligibleBenchIndex, which
      // skips anyone ejected earlier: normally the assistant, and when the assistant
      // is gone too the next slot, into which setCoach stamps the captain.
      takeoverText: function(type, newCoachIndex) {
        var b = newCoachIndex === null ? null : this.bench(type, newCoachIndex);
        if(!b || !b.firstName || !b.lastName) {
          return "Ekipa je ostala brez trenerja — vodenje prevzame kapetan.";
        }

        // the full name, as the other crit dialogs give it — initials would read
        // "kapetan P4 I.." once the sentence's own full stop lands on them
        var name = scoresheet.name(b.firstName, b.lastName);
        if(this.isOriginallyAssistant(b)) return "Vodenje ekipe prevzame pomočnik trenerja " + name + ".";
        if(b.pid) return "Pomočnika ni več — vodenje ekipe prevzame kapetan " + name + ".";
        return "Vodenje ekipe prevzame " + name + ".";
      },
      isOT: function() {
        return scoresheet.isOT(this.quarter);
      },
      isMinuteSelected: function(min) {
        return this.minute === min;
      },
      isFirstLogInQuarter: function(quarter) {
        for(log of this.logs) {
          if(log.fn == 'changeConfirmTeamSelection') continue;
          if(log.quarter == quarter) return false;
        }
        return true;
      },
      type: function(pid) {
        return scoresheet.player(pid).type;
      },
      pointsText: function(pid) {
        var n = this.points(pid);
        // a zero carries no information — fifteen tiles saying "0 točk" is noise
        if(n == 0) {
          return "";
        }
        if(n%100 == 1) {
          return n + " točka";
        } else if(n%100 == 2) {
          return n + " točki";
        } else if(n%100 == 3 || n%100 == 4) {
          return n + " točke";
        } else {
          return n + " točk";
        }
      },
      points: function(pid) {
        var p = this[pid];
        return p.ft + 2*p.pt2 + 3*p.pt3;
      },
      validateFouledOut: function(pid) {
        var is = this.isFouledOut(pid);
        if(is) {
          alerts.warning("<p><b>" + scoresheet.playerNameWithNumber(pid) + "</b> ima 5 osebnih napak in ne sme več igrati.</p>");
        } 
        return !is;
      },
      // Players eligible to take the armband: the ones on the sheet for this team
      // who are still in the match — the same set the floor shows, minus anyone
      // ejected (the ejected ex-captain keeps isCaptain, which is why the team is
      // captainless in the first place).
      captainCandidates: function(type) {
        var list = [];
        for(pid in scoresheet.data.playerId2data) {
          if(scoresheet.player(pid).type !== type) continue;
          if(this.isEjected(pid)) continue;

          list.push(pid);
        }
        return list.sort(function(a, b) {
          return parseInt(scoresheet.player(a).number, 10) - parseInt(scoresheet.player(b).number, 10);
        });
      },
      // Raise the captain dialog for every team that has none. Called after the
      // scoresheet loads, so a refresh cannot escape the choice.
      demandCaptains: function() {
        if(this.matchFinished) return;

        for(var type of ['home', 'away']) {
          this.validateWithoutCaptain(type);
        }
      },
      validateWithoutCaptain: function(type, hidePopup) {
        var is = this.isWithoutCaptain(type);
        if(is && !hidePopup) {
          // The players are listed IN the dialog: it used to say "izberi novega
          // kapetana" and then wait for a click on the floor, which nothing on
          // screen asked for. Clicking a card still works — this is the obvious
          // path, not a replacement.
          var html = "<p><b>" + this.complaint[type].teamName + "</b> je brez kapetana. Izberi novega kapetana.</p>";
          html += "<div class='captain-pick'>";
          for(var pid of this.captainCandidates(type)) {
            html += "<button type='button' class='captain-pick__opt js-pick-captain btn-" + type + "' data-type='" + type + "' data-pid='" + pid + "'>";
            html += "<span class='captain-pick__num'>" + (scoresheet.player(pid).number || '') + "</span>";
            html += "<span class='captain-pick__name'>" + scoresheet.playerName(pid) + "</span>";
            html += "</button>";
          }
          html += "</div>";
          // It asks for a choice now, so it is titled as one rather than "POZOR!" —
          // and it demands one: no continue button, no ×, no esc. The exception is
          // a team with nobody left to take the armband, where an inescapable
          // dialog would be a trap rather than a rule.
          alerts.warning(html, {
            title: 'Izberi kapetana',
            modalClass: 'dialog--captain',
            // one dialog per team, however many entries are refused meanwhile
            key: 'captain-' + type,
            dismissible: this.captainCandidates(type).length === 0
          });
        }
        return !is
      },
      validateEligible: function(pid, after) {
        var is = this.isEjected(pid);
        if(is) {
          // The number badge wears the player's ACTUAL jersey colour (the generated
          // .btn-home/.btn-away), not the alert red — a red block with a white number
          // is exactly the form of a red team's tile, and the badge should answer
          // "which team's #4", which the sentence now says in words too.
          var ejType = scoresheet.player(pid).type;
          // If he was also coaching, the team has just lost its coach as well, and
          // that is the fact the keeper has to act on — so this dialog says it, and
          // names who takes the bench. Reached from this side when the last entry
          // was his as a PLAYER; the bench dialog says the same from the other side.
          var coachIdx = this.benchIndexForPlayer(ejType, pid);
          var html =
            "<p class='crit__who'><span class='crit__num btn-" + ejType + "'>" + (scoresheet.player(pid).number || '') + "</span> " +
              (coachIdx === null ? "" : "Igralec/trener ") + scoresheet.playerName(pid) + "</p>" +
            "<p class='crit__what'>(" + this.complaint[ejType].teamName + ") je izključen iz tekme.</p>" +
            "<p class='crit__rule'>Igralec mora zapustiti igrišče in klop.</p>";
          if(coachIdx !== null) {
            html += "<p class='crit__rule'>Ne sme več igrati niti voditi ekipe.</p>" +
                    "<p class='crit__rule'>" + this.takeoverText(ejType, this.nextEligibleBenchIndex(ejType, coachIdx + 1)) + "</p>";
          }
          alerts.warning(html, { title: "Izključitev", continueLabel: "RAZUMEM", modalClass: "dialog--crit" });
        } 
        return !is;
      },
      validateBenchEligible: function(type, index) {
        var is = this.isBenchEjected(type, index);
        if(is) alerts.warning(
          "<p class='crit__who'>" + this.altBenchName(type, index) + "</p>" +
          "<p class='crit__what'>(" + this.complaint[type].teamName + ") je izključen iz tekme.</p>" +
          "<p class='crit__rule'>Oseba mora zapustiti klop.</p>",
          { title: "Izključitev", continueLabel: "RAZUMEM", modalClass: "dialog--crit" }
        );
        return !is;
      },
      validateTimeouts: function(type) {
        var is = this.getTimeoutsLeft(type) > 0;
        if(!is) alerts.warning("<p><b>" + this.complaint[type].teamName + "</b> je porabila vse minute odmora.</p>");
        return is;
      },
      validateChallenge: function(type) {
        var is = this.getChallengesLeft(type) > 0;
        if(!is) alerts.warning("<p><b>" + this.complaint[type].teamName + "</b> je porabila trenerski izziv (HCC).</p>");
        return is;
      },
      validateMatchFinished: function() {
        var is = !this.matchFinished;
        if(!is) alerts.warning("<p>Tekma je končana — novih vpisov ni več mogoče dodajati. Popravke narediš v Dogodkih.</p>");
        return is;
      },
      validateNextOffense: function() {
        var is = this.isFirstOffenseSet();
        if(!is) alerts.warning("<p>Najprej izberi ekipo, ki ima prvi napad — vprašanje je nad igriščem.</p>");
        return is;
      },
      addChangeQuarterLog: function(fn, diff) {
        var log = {
          fn: fn,
          quarter: this.quarter-diff,
          diff: diff,
          inverseArgs: [-diff, true]
        };
        return this.addLog(log);
      },
      addFinishMatchLog: function(fn) {
        var log = {
          fn: fn,
          inverseArgs: [true]
        };
        return this.addLog(log, this.quarter-1, this.maxMinute(this.quarter-1));
      },
      maxMinute: function(q) {
        if(q <= 4) {
          return 10;
        } else {
          return 5;
        }
      },
      addChangeCaptainLog: function(type, pid) {
        var log = {
          fn: "changeCaptain",
          type: type, 
          pid: pid,
          inverseArgs: [type, pid, true]
        };
        return this.addLog(log);
      },
      addFtLog: function(type, pid, diff, ftType, undoPrevious) {
        var log = {
          fn: "changeScore",
          type: type,
          pid: pid,
          diff: diff,
          ftType: ftType,
          undoPrevious: undoPrevious,
          inverseArgs: [type, -diff, pid, true]
        };
        return this.addLog(log);
      },
      addCancelFtLog: function(type, pid, diff, ftType, undoPrevious) {
        var log = {
          fn: "cancelFt",
          type: type,
          pid: pid,
          diff: diff,
          ftType: ftType,
          undoPrevious: undoPrevious,
          inverseArgs: [type, -diff, pid, true]
        };
        return this.addLog(log);
      },
      addChangeLog: function(fn, type, pid, diff, undoPrevious) {
        var log = {
          fn: fn,
          type: type,
          pid: pid,
          diff: diff,
          undoPrevious: undoPrevious,
          inverseArgs: [type, -diff, pid, true]
        };
        return this.addLog(log);
      },
      addChangeBenchLog: function(fn, type, diff, coachIndex, newCoachIndex, newOtherIndex, mark, staffType, undoPrevious) {
        var log = {
          fn: fn,
          type: type,
          diff: diff,
          staffType: staffType,
          coachIndex: coachIndex,
          newCoachIndex: newCoachIndex,
          newOtherIndex: newOtherIndex,
          mark: mark,
          undoPrevious: undoPrevious,
          inverseArgs: [type, -diff, coachIndex, newCoachIndex, newOtherIndex, mark]
        };
        return this.addLog(log);
      },
      addFirstOffenseLog: function(type) {
        var log = {
          fn: "setFirstOffense",
          type: type,
          inverseArgs: [null, true]
        };
        return this.addLog(log);
      },
      addNextOffenseLog: function(newType) {
        var log = {
          fn: "changeOffense",
          type: newType,
          inverseArgs: [true]
        };
        return this.addLog(log);
      },
      addChangeStarting5Log: function(pid, starting5) {
        var log = {
          fn: "changeStarting5",
          pid: pid,
          type: this.type(pid),
          starting5: starting5
        };
        return this.addLog(log);
      },
      addChangePlayedLog: function(pid) {
        var log = {
          fn: "changePlayed",
          pid: pid,
          type: this.type(pid),
          played: this.hasPlayed(pid),
          inverseArgs: [pid, true]
        };
        return this.addLog(log);
      },
      addLog: function(log, q, m) {
        log = $.extend({ id: this.nextId(), minute: m || this.minute, quarter: q || this.quarter }, log);
        if(log.quarter > 1 && log.fn != 'changeOffense' && log.fn != 'changeConfirmTeamSelection' && this.isFirstLogInQuarter(log.quarter)) {
          var con = this;
          var callback = function() {
            con.changeOffense();
          };
          alerts.confirmation({ title: "POZOR", body: "<p>Puščica po začetku četrtine še ni bila obrnjena. Ali jo želiš obrniti?</p>", yesCallback: callback });
        }
        this.logs.push(log);
        refreshContext();
        scoresheet.save();
        return log;
      },
      nextId: function() {
        return ++this.logId;
      },
      undoLog: function(log) {
        if(log.inverseArgs) {
          var undoFn = log.fn + (this[log.fn+"Undo"] ? "Undo" : "")
          this[undoFn](...log.inverseArgs);
          this.endAction();
        }
      },
      // "Odstrani zadnji vpis" takes the last LINE out of the log. What that
      // costs depends on whether the line still counts:
      //
      //   standing    — it counts, so its effect has to come off with it
      //                 (undoLog applies the inverse). Needs inverseArgs.
      //   struck out  — Izbriši already replayed the log without it, so the
      //                 sheet does not move at all. Only the crossed-out line
      //                 goes. Always allowed.
      //
      // Running both was the bug: undo popped the tombstone AND applied the
      // inverse a second time. Measured, two baskets: 4 -> Izbriši -> 2 ->
      // Odstrani -> 0, with a standing basket still in the log. The screen said
      // 0 and the log said 2, and the log is what the PDF is printed from
      // (ADR-0001).
      //
      // A superseded entry (editedById points at a replacement, not at DELETED)
      // is refused: its replacement stands on the sheet, and removing the
      // original breaks the chain the report follows to keep a commissioner's
      // label attached to a foul that was reassigned.
      isLastLogStruckOut: function() {
        var last = this.reversedLog(0);
        return !!(last && last.editedById === "DELETED");
      },
      canBeUndone: function() {
        var last = this.reversedLog(0);
        if(!last) return false;
        if(last.editedById) return last.editedById === "DELETED";

        return !!last.inverseArgs;
      },
      // Why the button is dead, for its title — the same courtesy the prematch's
      // disabled confirm buttons pay.
      undoWhy: function() {
        if(this.canBeUndone()) return "";
        if(!this.hasLogs()) return "Ni še nobenega vpisa.";
        if(this.reversedLog(0).editedById) {
          return "Zadnji vpis je popravljen — na zapisniku velja popravek. Uredi ga v Dogodkih.";
        }
        return "Tega vpisa ni mogoče odstraniti.";
      },
      undo: function() {
        if(this.canBeUndone()) {
          var log = this.logs.pop();
          // A crossed-out line is not on the sheet: taking its effect off again
          // would take it off twice. Only the line goes.
          if(!log.editedById) {
            this.undoLog(log);
            this.undoCancelledFouls(log);
          }

          if(this.hasLogs() && log.undoPrevious) {
            this.undo();
          }

          this.reset();
          scoresheet.save();
        }
      },
      hasLogs: function() {
        return this.logs.length > 0;
      },
      reversedLog: function(i) {
        if(this.logs.length > i) {
          return this.logs[this.logs.length - 1 - i];
        } else {
          return null;
        }
      },
      doFiltersApply: function(log) {
        for(filter in this.logTable.filters) {
          var fval = this.logTable.filters[filter];
          if(!fval) continue;

          var lval;
          if(filter === 'state') {
            lval = scoresheet.data.logs.actions[log.fn].name(log) + (log.fn == 'changeScore' ? "|" + log.diff : "");
          } else if(filter === 'number') {
            var p = scoresheet.data.playerId2data[log.pid];
            lval = p && p.number;
          } else if(filter === 'entity') {
            lval = log.pid || log.staffType;
          } else {
            lval = log[filter];
          }

          if(fval != lval) {
            return false;
          }
        }
        return true;
      },
      changePage: function(diff) {
        var newPage = this.currentLogPage() + diff; 
        if(diff < 0 && newPage < 1) {
          newPage = 1;
        } else if(diff > 0) {
          var maxPage = this.getLogTableMaxPage();
          if(newPage > maxPage) {
            newPage = maxPage;
          }
        }
        setContext("logTable.page", newPage);
      },
      isOfficialMissing: function(role) {
        return this.referees && this.referees[role] && this.referees[role].status === "missing";
      },
      // An official can have a slot and no name: picking "z licenco" sets the status
      // before the licence lookup answers, and it may answer with nothing. The three
      // guards below — no referees, no entry, status "missing" — all pass in that
      // state, so the name used to come out as `undefined + " " + undefined`.
      //
      // Half a name is no name either: "Janez undefined" is the same bug wearing a
      // first name. Same rule prematch already applies (pre_match.js#hasPersonName).
      hasOfficialName: function(role) {
        var o = this.referees && this.referees[role];
        return !!(o && o.first_name && o.last_name);
      },
      officialName: function(role) {
        if(this.isOfficialMissing(role) || !this.hasOfficialName(role)) {
          return "";
        } else {
          var s = this.referees[role].first_name + " " + this.referees[role].last_name;
          if(this.referees[role].city) {
            s += " (" + this.referees[role].city + ")";
          }
          return s;
        }
      },
      getObject: function(arr) {
        var obj = this;
        for(var key of arr) {
          obj = obj[key];
          if(!obj) break;
        }
        return obj;
      },
      shouldPersonSign: function(arr) {
        var obj = this.getObject(arr);
        return obj && obj.sig && !this.hasPersonSigned(arr);
      },
      hasPersonSigned: function(arr) {
        var obj = this.getObject(arr);
        return obj && obj.sig && obj.sig.status == 'SIGNED';
      },
      personSignedText: function(arr) {
        if(this.hasPersonSigned(arr)) {
          return "PODPISANO";
        } else {
          return "ČAKA NA PODPIS";
        }
      },
      canShowQrCode: function(arr) {
        var obj = this.getObject(arr);
        return obj && obj.sig;
      },
      showQrCode: function(arr) {
        if(this.canShowQrCode(arr)) {
          var person = this.getObject(arr);
          // pushes to the paired phone when there is one; the modal always
          // carries the QR code too (see signing.js)
          signing.request({ name: person.first_name + " " + person.last_name, uuid: person.sig.uuid }, scoresheet.data.signatureUrl);
        }
      },
      confirmSigned: function(uuid) {
        for(d of [ ['referees', 'referee1'], ['referees', 'referee2'], ['referees', 'referee3'], ['complaint', 'home', 'captain'], ['complaint', 'away', 'captain'] ]) {
          d.push('sig');
          var sig = this.getObject(d);
          if(sig && sig.uuid == uuid) {
            sig.status = 'SIGNED';
            scoresheet.save();
            var $modal = $('.modal.show');
            if($modal.length > 0) {
              if($modal.find('#'+uuid).length > 0) {
                $modal.modal('hide');
              }
            }
            refreshContext();
            return;
          }
        }
      },
      // Time only, for the save chip in the match bar. The full locale datetime this
      // used to return was fine at the foot of the page; in a 48px bar during a
      // match, the date is noise — the save either just happened or it did not.
      getLastServerSaveTimeNice: function() {
        if(this.serverSaveTime) {
          return new Date(this.serverSaveTime).toLocaleTimeString("sl-SI", { hour: '2-digit', minute: '2-digit' });
        } else {
          return "/";
        }
      },
      complaintQuestionText: function(side) {
        if(!this.complaint || !this.complaint[side] || !this.complaint[side].captain) {
          return "";
        }
        if(side == 'home') {
          return "Ali kapetan domačih (" + this.complaint.home.teamName + ") vlaga pritožbo?";
        } else {
          return "Ali kapetan gostov (" + this.complaint.away.teamName + ") vlaga pritožbo?";
        }
      },
      captainName: function(side) {
        if(!this.complaint || !this.complaint[side].captain) {
          return "";
        }
        return this.complaint[side].captain.first_name + " " + this.complaint[side].captain.last_name;
      },
      showReportButton: function() {
        return this.matchFinished && ['home', 'away'].every( side => this.isComplaintOk(side) ) && ['referee1', 'referee2', 'referee3'].every( ref => !this.referees[ref] || this.referees[ref].status == 'missing' || this.hasPersonSigned(['referees', ref]) );
      },
      isComplaintOk: function(side) {
        return this.complaint[side].isFiled == 'no' || this.hasPersonSigned(['complaint', side, 'captain']);
      },
      setPage: function(page) {
        setContext("logTable.page", page);
      },
      isLogFeasible: function(log) {
        if(!log) return false;
        if(this.logTable.onlyFlagged && !log.flagged) return false;
        return this.doFiltersApply( log );
      },
      isLogTablePageSelected: function(page) {
        return this.currentLogPage() === page;
      },
      // --- which entries the table is showing -----------------------------------
      //
      // Every entry that passes the filters, newest first. Built ONCE per refresh
      // and reused by every row.
      //
      // It used to be derived per row: showLog(i) took the i-th entry counting back
      // from the newest, then walked the whole log to work out its position among the
      // filtered ones. 301 rows × the whole log, on every refreshContext — so once
      // per keeper action, O(n²) in the length of the match.
      //
      // The cache turns over on scoresheet.uiTick, which refreshContext bumps, AND on
      // the filters, which change without it: a filter select is a two-way twine
      // binding, so the keeper's pick lands in the context and twine refreshes the
      // bindings itself, never calling refreshContext. The tick alone therefore held
      // the cache across a filter change and the table did not move.
      //
      // The tick is still the right granularity for everything else, precisely because
      // nothing else on this screen changes without a refresh: it is driven by the
      // local context, never by a save or a reply from the server, so the table keeps
      // up with the keeper on a dead connection exactly as it does on a live one. The
      // extra guards (the array identity and its length) are there for a mutation that
      // somehow skips the refresh.
      logFilterKey: function() {
        var parts = [this.logTable.onlyFlagged ? 'F' : ''];
        for(filter in this.logTable.filters) {
          parts.push(this.logTable.filters[filter]);
        }
        return parts.join('|');
      },
      feasibleLogs: function() {
        var cache = scoresheet.logCache;
        var filters = this.logFilterKey();
        if(cache.tick === scoresheet.uiTick &&
           cache.filters === filters &&
           cache.source === this.logs &&
           cache.length === this.logs.length) {
          return cache.rows;
        }

        var rows = [];
        for(var i = this.logs.length - 1; i >= 0; i--) {
          if(this.isLogFeasible(this.logs[i])) rows.push(this.logs[i]);
        }

        cache.tick = scoresheet.uiTick;
        cache.filters = filters;
        cache.source = this.logs;
        cache.length = this.logs.length;
        cache.rows = rows;
        return rows;
      },
      // The entry drawn in row i of the CURRENT PAGE — which is what every row
      // function below asks for, and what makes the table's size the page size
      // rather than a guess at how long a match can get.
      // The page the table actually draws. `logTable.page` is what the keeper last
      // picked; a filter can shrink the log under it, and a pick that no longer
      // exists would draw an empty table with no hint of why. Clamped on READ rather
      // than written back, for two reasons: the pick survives, so widening the filter
      // again puts the keeper back where they were; and a filter select writes into
      // the context through twine's own change handler, so there is no moment of ours
      // in which a write-back would reliably happen before the table is redrawn.
      currentLogPage: function() {
        var max = Math.max(1, this.getLogTableMaxPage());
        return Math.min(Math.max(1, this.logTable.page), max);
      },
      rowLog: function(i) {
        var rows = this.feasibleLogs();
        return rows[(this.currentLogPage() - 1) * this.logTable.limit + i] || null;
      },
      showLog: function(i) {
        return i < this.logTable.limit && !!this.rowLog(i);
      },
      clearLogFilters: function() {
        for(filter in this.logTable.filters) {
          this.logTable.filters[filter] = "";
        }
        setContext("logTable.onlyFlagged", false);
        refreshContext();
      },
      // --- flags: the assistant referee's own marks ------------------------------
      // A flag is a note to self, not a correction: it toggles in place on the log
      // entry, adds no entry and sets no editedById, so what the sheet says happened
      // is untouched. It rides in the scoresheet JSON the client already PUTs whole,
      // which is what makes it survive a reload and reach the follow page's server.
      // Nothing on the Ruby side reads it (ScoresheetLog selects by fn) and no PDF
      // template renders it.
      isLogFlagged: function(i) {
        var log = this.rowLog(i);
        return !!(log && log.flagged);
      },
      toggleLogFlag: function(i) {
        var log = this.rowLog(i);
        if(!log) return;

        log.flagged = !log.flagged;
        // a page whose rows all vanish under the filter would strand the keeper
        if(this.logTable.onlyFlagged && this.logTable.page > this.getLogTableMaxPage()) {
          setContext("logTable.page", Math.max(1, this.getLogTableMaxPage()));
        }
        refreshContext();
        scoresheet.save();
      },
      flaggedCount: function() {
        return this.logs.filter(function(log) { return log.flagged; }).length;
      },
      hasFlaggedLogs: function() {
        return this.flaggedCount() > 0;
      },
      // exception-first: the count appears only once something is marked
      flagFilterLabel: function() {
        return this.hasFlaggedLogs() ? "Samo označeni (" + this.flaggedCount() + ")" : "Samo označeni";
      },
      toggleOnlyFlagged: function() {
        setContext("logTable.onlyFlagged", !this.logTable.onlyFlagged);
        setContext("logTable.page", 1);
        refreshContext();
      },
      getLogTableMaxPage: function() {
        return Math.ceil(this.getFeasibleLogCount() / this.logTable.limit);
      },
      getFeasibleLogCount: function() {
        return this.feasibleLogs().length;
      },
      isLogTablePageVisible: function(page) {
        return page <= this.getLogTableMaxPage();
      },
      isLogEdited: function(i) {
        var log = this.rowLog(i);
        return log && log.editedById;
      },
      logArrayQuarter: function(i) {
        var log = this.rowLog(i);
        return log ? scoresheet.quarterName(log.quarter) : "";
      },
      logArrayMinute: function(i) {
        var log = this.rowLog(i);
        return log ? log.minute + "'" : "";
      },
      logArrayAction: function(i) {
        var log = this.rowLog(i);
        return log ? this.logAction(log) : "";
      },
      logArrayTeam: function(i) {
        var log = this.rowLog(i);
        if(log && log.type) {
          return scoresheet.data.side2type2data[log.type].name;
        } else {
          return "";
        }
      },
      logArrayNumber: function(i) {
        var log = this.rowLog(i);
        var p = log && log.pid ? scoresheet.player(log.pid) : null;
        return p ? p.number : "";
      },
      logArrayEntity: function(i) {
        var log = this.rowLog(i);
        if(log) { 
          if(log.pid) {
            return scoresheet.playerName(log.pid);
          } else if([scoresheet.data.COACH, scoresheet.data.ASSISTANT, scoresheet.data.OTHER].indexOf(log.staffType) >= 0) {
            return scoresheet.data.logs[log.staffType];
          } else {
            return "";
          }
        } else {
          return "";
        }
      },
      editActionText: function() {
        return this.edit &&  this.edit.log ? this.logAction(this.edit.log) : "";
      },
      logAction: function(log) {
        return scoresheet.data.logs.actions[log.fn].name(log);
      },
      logActionText: function(log) {
        return scoresheet.data.logs.actions[log.fn].text(log);
      },
      removeLog: function(i) {
        var log = this.rowLog(i);
        var con = this;
        alerts.confirmation({ type: 'warning', title: 'Potrditev', body: '<p><b>Izbriši vpis?</b></p><p>' + this.logActionText(log) + '</p><p>Vpis ostane v zapisniku prečrtan.</p>', yesCallback: function() {
          var before = con.eligibilitySnapshot();
          log.editedById = "DELETED";
          con.rebuildFromLogs();
          con.announceEligibilityChanges(before);
          scoresheet.save();
        }});
      },
      // Only a DELETED entry can come back, and only from its own row.
      //
      // A corrected one cannot: its replacement is standing, so restoring the
      // original would mean the event happened twice. And this is not what
      // "Odstrani zadnji vpis" does — that button takes the last LINE out of the
      // log, crossed out or not. Povrni puts a crossed-out one back ON the sheet,
      // and works on any of them, however old.
      canRestoreLog: function(i) {
        var log = this.rowLog(i);
        return !!(log && log.editedById === "DELETED");
      },
      restoreLog: function(i) {
        var log = this.rowLog(i);
        var con = this;
        alerts.confirmation({ title: 'Potrditev', body: '<p><b>Povrni vpis?</b></p><p>' + this.logActionText(log) + '</p><p>Vpis se vrne na zapisnik in ne bo več prečrtan.</p>', yesCallback: function() {
          var before = con.eligibilitySnapshot();
          delete log.editedById;
          con.rebuildFromLogs();
          con.announceEligibilityChanges(before);
          scoresheet.save();
        }});
      },
      editLog: function(i) {
        var log = this.rowLog(i);
        this.edit = {
          log: log,
          changedLog: $.extend({}, log, { id: this.nextId(), note: "" }),
          index: this.logIndexById(log.id)
        };
        scoresheet.buildEditSelects();
        refreshContext();
        this.openEditLogModal();
      },
      insertLogBefore: function(i) {
        var log = this.rowLog(i);
        this.new = {
          newLog: $.extend({}, { id: this.nextId(), note: "" }),
          index: this.logIndexById(log.id)
        };
        scoresheet.buildNewSelects();
        refreshContext();
        this.openNewLogModal();
      },
      openEditLogModal: function() {
        $('#edit-log-modal').modal('show');
      },
      closeEditLogModal: function() {
        $('#edit-log-modal').modal('hide');
      },
      openNewLogModal: function() {
        $('#new-log-modal').modal('show');
      },
      closeNewLogModal: function() {
        $('#new-log-modal').modal('hide');
      },
      openFtModal: function() {
        $('#ft-modal').modal('show');
      },
      closeFtModal: function(reset) {
        $('#ft-modal').modal('hide');
        if(reset) {        
          this.ft = null;
          this.reset();
        }
      },
      canRemoveLog: function(i) {
        var log = this.rowLog(i);
        return log && !this.isLogEdited(i);
      },
      canEditLog: function(i) {
        var log = this.rowLog(i);
        return log && !this.isLogEdited(i) && ["changeStarting5", "changeCaptain", "setFirstOffense", "changePlayed", "changeScore", "removeTimeout", "changeTimeouts", "changeChallenge"].concat(scoresheet.changeFoulsActions()).indexOf(log.fn) >= 0;
      },
      getValue: function(val) {
        var intVal = parseInt(val);
        if(val && !isNaN(intVal)) {
          return intVal;
        } else if(val == 'true') {
          return true;
        } else if(val == 'false') {
          return false;
        } else {
          return val;
        }
      },
      saveEditLog: function() {
        var l = {};
        for(key in this.edit.changedLog) {
          if(key == 'inverseArgs') continue;

          var val = this.edit.changedLog[key];
          if(val == null || val == undefined) continue;

          l[key] = this.getValue(val);
        }
        var before = this.eligibilitySnapshot();
        this.updateOriginalLog(this.edit.log);
        this.logs.splice(this.edit.index+1, 0, l);
        this.edit = {};

        this.rebuildFromLogs();
        this.announceEligibilityChanges(before);
        scoresheet.save();
        this.closeEditLogModal();
      },
      prepareNewLog: function() {
        var l = {};
        for(key in this.new.newLog) {
          var val = this.new.newLog[key];
          if(val == null || val == undefined) continue;

          if(key == 'fn') {
            var tmp = val.split('|');
            if(tmp.length == 1) {
              l[key] = this.getValue(val);
            } else {
              l[key] = this.getValue(tmp[0]);
              l.diff = this.getValue(tmp[1]);
            }
          } else {
            l[key] = this.getValue(val);
          }
        }
        if(l.diff != 0 && !l.diff) {
          l.diff = 1;
        }
        if(l.benchValue) {
          l.coachIndex = this.coachIndex(l.type);
          l.mark = l.benchValue == scoresheet.data.COACH ? 'C' : 'B';
          l.benchIndex = this.benchIndex(l.type, l.benchValue);
          l.staffType = l.fn == 'changeBenchTFouls' ? scoresheet.data.COACH : l.benchValue;
        }
        return l;
      },
      isNewLogValid: function() {
        var log = this.prepareNewLog();
        var valid = log.fn && log.quarter && log.minute && (!this.actionNeedsType(log.fn) || log.type) && (!this.actionNeedsPid(log.fn) || log.pid) && (!this.isFreeThrow(log) || log.ftType) && (!this.actionNeedsCoachOrBench(log.fn) || log.benchValue);
        return valid;
      },
      saveNewLog: function() {
        var before = this.eligibilitySnapshot();
        this.logs.splice(this.new.index, 0, this.prepareNewLog());
        this.new = {};

        this.rebuildFromLogs();
        this.announceEligibilityChanges(before);
        scoresheet.save();
        this.closeNewLogModal();
      },
      updateOriginalLog: function(log) {
        log.editedById = this.edit.changedLog.id;
        if(log.fn == 'changeCaptain') {
          this[log.pid].isCaptain = false;
        }
      },
      actionNeedsPid: function(fn) {
        if(!fn) return false;
        var action = fn.split("|")[0];
        return ["changeScore", "cancelFt", "changePlayed", "changeStarting5"].concat(scoresheet.changeFoulsActions()).indexOf(action) >= 0;
      },
      actionNeedsType: function(fn) {
        if(!fn) return false;
        var action = fn.split("|")[0];
        return ["changeQuarter"].indexOf(action) < 0;
      },
      actionNeedsCoachOrBench: function(fn) {
        if(!fn) return false;
        var action = fn.split("|")[0];
        return ['changeBenchTFouls', 'changeBenchEjections'].indexOf(action) >= 0;
      },
      showEditPlayers: function(type) {
        return this.edit && this.edit.changedLog && this.edit.changedLog.pid && this.edit.changedLog.type == type;
      },
      showNewPlayers: function(type) {
        return this.new && this.new.newLog && this.new.newLog.type == type && this.actionNeedsPid(this.new.newLog.fn);
      },
      showNewCoachAndBench: function(type) {
        return this.new && this.new.newLog && this.new.newLog.type == type && this.actionNeedsCoachOrBench(this.new.newLog.fn);
      },
      showEditFtType: function() {
        return this.edit && this.edit.changedLog && this.edit.changedLog.ftType;
      },
      showNewFtType: function() {
        if(this.new && this.new.newLog && this.new.newLog.fn) {
          var tmp = this.new.newLog.fn.split('|');
          return this.isFreeThrow({ fn: tmp[0], diff: tmp.length > 1 ? tmp[1] : null });
        } else {
          return false;
        }
      },
      showNewType: function() {
        return this.new && this.new.newLog && this.actionNeedsType(this.new.newLog.fn);
      },
      canEditFn: function() {
        return this.edit && this.edit.log && ["changeScore"].concat(scoresheet.changeFoulsActions()).indexOf(this.edit.log.fn) >= 0;
      },
      isEditChange23Score: function() {
        return this.edit && this.edit.log && this.edit.log.fn == "changeScore" && [2,3].indexOf(this.edit.log.diff) >= 0;
      },
      isEditChangeFt: function() {
        return this.edit && this.edit.log && this.edit.log.fn == "changeScore" && [0,1].indexOf(this.edit.log.diff) >= 0;
      },
      isEditChangeFouls: function() {
        return this.edit && this.edit.log && scoresheet.changeFoulsActions().indexOf(this.edit.log.fn) >= 0;
      },
      isFoul: function(log) {
        return this.isSpecialFoul(log) || ['changeFouls', 'changeFTFouls'].indexOf(log.fn) >= 0;
      },
      isSpecialFoul: function(log) {
        return this.isTechnicalFoul(log) || this.isOtherSpecialFoul(log);
      },
      isTechnicalFoul: function(log) {
        return ['changeTFouls', 'changeTech1Fouls', 'changeTech2Fouls', 'changeBenchTFouls'].indexOf(log.fn) >= 0;
      },
      isOtherSpecialFoul: function(log) {
        return ['changeUFouls', 'changeFlagrantFouls', 'changeDisruptiveFouls',
                'changeEjections', 'changeBenchEjections', 'changeFights'].indexOf(log.fn) >= 0;
      },
      isFreeThrow: function(log) {
        return (log.fn == 'changeScore' && log.diff <= 1) || log.fn == 'cancelFt';
      },
      isMinuteVisible: function(i) {
        return i <= scoresheet.data.minutesPerOT || !this.isOT();
      },
      specialFouls: function() {
        var specials = [];
        for(var i = this.logs.length - 1; i >= 0; i--) {
          var log = this.logs[i];
          if(this.isFreeThrow(log)) {
            break;
          }
          if(this.isSpecialFoul(log) && !log.undoPrevious) {
            specials.splice(0, 0, log);
          }
        }
        return specials;
      },
      haveSamePenalty: function(log1, log2) {
        return log1.minute == log2.minute && log1.quarter == log2.quarter && log1.type != log2.type && ( ( this.isTechnicalFoul(log1) && this.isTechnicalFoul(log2) ) || ( this.isOtherSpecialFoul(log1) && this.isOtherSpecialFoul(log2) ) );
      },
      logById: function(id) {
        for(var i = this.logs.length-1; i >= 0; i--) {
          var log = this.logs[i];
          if(log.id == id) return log;
        }
        return null;
      },
      logIndexById: function(id) {
        for(var i = this.logs.length-1; i >= 0; i--) {
          var log = this.logs[i];
          if(log.id == id) return i;
        }
        return null;
      },
      undoCancelledFouls: function(log) {
        if(log.cancelledWithId) {
          var log2 = this.logById(log.cancelledWithId);
          log2.cancelledWithId = null;
          log.cancelledWithId = null;
        }
      },
      // The question both cancellation checks share. The keeper confirms, the two
      // entries point at each other, and the sheet prints a C beside both. `why`
      // is an optional rule-of-thumb paragraph — the and-one check passes one,
      // because there the answer depends on a fact the log does not carry.
      offerCancellation: function(log1, log2, why) {
        var txt = "<p><b>Ali se kazni za naslednji napaki izničita?</b></p>";
        txt += "<ul class='list-unstyled'>";
        txt += "<li>"+ this.logActionText(log1) +"</li>";
        txt += "<li>"+ this.logActionText(log2) +"</li>";
        txt += "</ul>";
        if(why) txt += "<p class='foul-kinds__why'>" + why + "</p>";
        var callback = function() {
          log1.cancelledWithId = log2.id;
          log2.cancelledWithId = log1.id;
          refreshContext()
          scoresheet.save();
        };
        alerts.confirmation({ title: "Izničenje kazni", body: txt, yesCallback: callback });
      },
      checkCancelledFouls: function() {
        var specials = this.specialFouls();
        if(specials.length == 0) return;

        var i = specials.length - 1;
        var lastSpecial = specials[i--];
        var special;

        while(i >= 0) {
          special = specials[i--];
          if(!special.cancelledWithId && this.haveSamePenalty(lastSpecial, special)) {
            this.offerCancellation(lastSpecial, special);
            return;
          }
        }

        this.checkAndOneCancelled(lastSpecial);
      },
      // The other cancellation FIBA allows and the sheet could not record: an
      // and-one — koš za 2/3 and a personal foul WITH free throws on the scorer —
      // with a technical against the scoring team before the free throw is taken.
      // One free throw each way, the penalties cancel, nobody shoots.
      //
      // Measured over 20 782 recorded matches (28 occurrences): the basket and the
      // foul arrive in either order — the keeper enters the foul first about a
      // fifth of the time — a timeout, a substitution or a video review may stand
      // between the three entries, and a third of the technicals are the bench's.
      // What the log CANNOT say is how many free throws the personal foul carries
      // (an and-one means one, but the entry names neither the count nor the
      // fouled player) — so this asks the keeper, exactly like the check above,
      // and never links on its own.
      checkAndOneCancelled: function(tech) {
        if(!this.isTechnicalFoul(tech) || tech.mark == 'BD' || tech.cancelledWithId) return;

        var neutral = ['changeTimeouts', 'removeTimeout', 'changePlayed', 'changeChallenge', 'changeOffense'];
        var pair = [];
        for(var i = this.logs.length - 1; i >= 0 && pair.length < 2; i--) {
          var log = this.logs[i];
          if(log === tech || log.editedById || log.undoPrevious) continue;
          if(neutral.indexOf(log.fn) >= 0) continue;
          var isBasket = log.fn == 'changeScore' && [2, 3].indexOf(Number(log.diff)) >= 0;
          if(!isBasket && log.fn != 'changeFTFouls') return;   // anything else ends the story
          pair.splice(0, 0, log);
        }
        if(pair.length < 2) return;

        var basket = pair[0].fn == 'changeScore' ? pair[0] : pair[1];
        var foul = pair[0].fn == 'changeFTFouls' ? pair[0] : pair[1];
        if(basket.fn != 'changeScore' || foul.fn != 'changeFTFouls') return;   // two of the same kind
        if(foul.cancelledWithId) return;
        if(basket.type != tech.type || foul.type == tech.type) return;

        var moment = function(log) { return log.quarter + "'" + log.minute; };
        if(moment(basket) != moment(tech) || moment(foul) != moment(tech)) return;

        this.offerCancellation(tech, foul,
          "<b>Izničita se</b>, če osebni napaki pripada en sam prosti met (koš je obveljal — dodatni met).<br>" +
          "<b>Ne izničita se</b>, če osebni napaki pripadata dva ali trije prosti meti.");
      },
      setBonus: function(type, isBonus, quarter) {
        var qkey = quarter >= 4 ? 'q4' : 'q' + quarter;
        setData(type+".bonus."+qkey, isBonus);
      },
      changeFoulsAction: function(con, type, diff, pid, quarter, undo) {
          var tkey = type+".fouls.q"+quarter;
          var n = getContext(tkey)+diff;
          setData(tkey, n);

          var pkey = pid+".fouls";
          setData(pkey, getContext(pkey)+diff);
          con.updatePlayed(pid);

          if(!undo) {
            con.setBonus(type, n >= scoresheet.data.bonus, quarter);
          }
          return n;
      },
      changeFouls: function(type, diff, pid, undo) {
        if(!undo && (!this.validateNextOffense() || !this.validateMatchFinished() || !this.validateEligible(pid))) return;  //  || !this.validateFouledOut(pid)

        var con = this; // we have to save the context
        this.waitingForMinute = function() {
          
          var n = con.changeFoulsAction(con, type, diff, pid, con.quarter, undo);

          if(undo) {
            con.setBonus(type, n >= scoresheet.data.bonus, con.quarter);
          } else {
            con.addChangeLog("changeFouls", type, pid, diff);
            con.checkEligibleToContinue(type, pid);
            if(n == scoresheet.data.bonus) {
              var callback = function() {
                con.setBonus(type, true, con.quarter);
                scoresheet.save();
              };
              alerts.confirmation({ title: "Bonus", type: "info", body: "<p>Ekipa <b>" + con.complaint[type].teamName + "</b> je v bonusu.</p><p>Vsaka nadaljnja obrambna osebna napaka pomeni <b>proste mete</b> (lahko 2 ali 3).</p>", continueCallback: callback });
            }
          }
          refreshContext();
        };
      },
      changeFTFoulsAction: function(con, type, diff, pid, quarter, undo) {
        var tkey = type+".fouls.q"+quarter;
        var n = getContext(tkey)+diff;
        setData(tkey, n);

        con.setBonus(type, n >= scoresheet.data.bonus, quarter);

        var pkey = pid+".fouls"
        var foulsCount = getContext(pkey);

        var typekey = pid+".foulTypes";
        if(undo) {
          getContext(typekey)[foulsCount-1] = "";
        } else {
          getContext(typekey)[foulsCount] = "|";
        }

        setData(pkey, foulsCount+diff);
        con.updatePlayed(pid);
      },
      changeFTFouls: function(type, diff, pid, undo) {
        if(!undo && (!this.validateNextOffense() || !this.validateMatchFinished() || !this.validateEligible(pid))) return;  //  || !this.validateFouledOut(pid)
        
        var con = this; // we have to save the context
        this.waitingForMinute = function() {

          con.changeFTFoulsAction(con, type, diff, pid, con.quarter, undo);

          if(!undo) { 
            con.addChangeLog("changeFTFouls", type, pid, diff);
            con.checkEligibleToContinue(type, pid);
          }
          refreshContext();
        };
      },
      changeCaptainAction: function(type, pid, undo) {
        var p = this[pid];
        p.isCaptain = !undo;
        var coach = this.coach(type);
        if(this.isOriginallyOther(coach) && !this.pid) {
          if(undo) {
            coach.firstName = null;
            coach.lastName = null;
            coach.pid = null;
          } else {
            this.setCoach(coach, true, type);
          }
        }
      },
      changeCaptain: function(type, pid, undo) {
        this.changeCaptainAction(type, pid, undo);
        if(!undo) this.addChangeCaptainLog(type, pid);
        this.reset();
      },
      cancelFtAction: function(con, type, diff, pid, undo) {
        var stype = scoresheet.field(diff);
        var pkey = pid+".cft";
        var factor = undo ? -1 : 1;
        setData(pkey, getContext(pkey)+factor);
        con.updatePlayed(pid);
      },
      cancelFt: function(type, diff, pid, undo) {
        if(!undo && (!this.validateNextOffense() || !this.validateMatchFinished() || !this.validateEligible(pid))) return;  //  || !this.validateFouledOut(pid)
        var con = this; // we have to save the context
        this.waitingForMinute = function() {
          con.cancelFtAction(con, type, diff, pid, undo);
          refreshContext();
        };
      },
      changeScoreAction: function(con, type, diff, pid, quarter, undo) {
        var skey = type+".score.q"+quarter;
        setData(skey, getContext(skey)+diff);

        var stype = scoresheet.field(diff);
        var pkey = pid+"."+stype;
        var factor = diff == 0 ? (undo ? -1 : 1) : scoresheet.factor(diff);
        setData(pkey, getContext(pkey)+factor);
        con.updatePlayed(pid);
      },
      changeScore: function(type, diff, pid, undo) {
        if(!undo && (!this.validateNextOffense() || !this.validateMatchFinished() || !this.validateEligible(pid))) return;  //  || !this.validateFouledOut(pid)

        var con = this; // we have to save the context
        this.waitingForMinute = function() {
          con.changeScoreAction(con, type, diff, pid, con.quarter, undo);

          if(!undo) {
            var log = con.addChangeLog("changeScore", type, pid, diff);
          }
          refreshContext();
        };
      },
      expectedFtType: function(ftType) {
        if(ftType == 'one_of_two') {
          return 'two_of_two';
        } else if(ftType == 'one_of_three') {
          return 'two_of_three';
        } else if(ftType == 'two_of_three') {
          return 'three_of_three';
        } else {
          return null;
        }
      },
      endFreeThrowAction: function(diff) {
        this.changeScoreAction(this, this.ft.type, diff, this.ft.pid, this.quarter);
        this.addFtLog(this.ft.type, this.ft.pid, diff, this.ft.ftType);
        var eFtType = this.expectedFtType(this.ft.ftType);
        if(eFtType) {
          this.closeFtModal(false);
          var self = this;
          setTimeout(function() {
            self.handleFreeThrow(self.ft.type, self.ft.pid, eFtType);
          }, 400);
        } else {
          this.closeFtModal(true);
        }
      },
      madeFt: function() {
        this.endFreeThrowAction(1);
      },
      missedFt: function() {
        this.endFreeThrowAction(0);
      },
      cancelledFt: function() {
        this.cancelFtAction(this, this.ft.type, 0, this.ft.pid);
        this.addCancelFtLog(this.ft.type, this.ft.pid, 0, this.ft.ftType);
        this.closeFtModal(true);
      },
      selectFtType: function(ftType) {
        setContext("ft.ftType", ftType);
      },
      checkSelectedFtType: function(ftType) {
        return this.ft && this.ft.ftType == ftType;
      },
      handleFreeThrow: function(type, pid, ftType) {
        if(!this.validateNextOffense() || !this.validateMatchFinished() || !this.validateEligible(pid)) return;

        this.ft = {
          ftType: ftType,
          type: type,
          pid: pid
        };
        refreshContext();
        this.openFtModal();
      },
      maySelectTimeout: function(type) {
        return this.getTimeoutsLeft(type) > 0 && this.isFirstOffenseSet();
      },
      maySelectChallenge: function(type) {
        return this.getChallengesLeft(type) > 0 && this.isFirstOffenseSet();
      },
      isChallengeTaken: function(type) {
        return this.getChallengesLeft(type) == 0;
      },
      // spent, as opposed to never granted: challengesLeft == 0 is also true on
      // competitions without HCC, and the scoreboard must not claim those spent one
      isChallengeSpent: function(type) {
        return scoresheet.data.challengeCount > 0 && this.isChallengeTaken(type);
      },
      mayRemoveTimeout: function(type) {
        return this.quarter == 4 && this.minute >= 9 && this.getTimeoutsLeft(type) == this.getTotalTimeouts();
      },
      validateTimeoutRemoval: function(type) {
        var is = this.mayRemoveTimeout(type);
        if(!is) alerts.warning("<p>Minute odmora ni mogoče odstraniti.</p>");
        return is;
      },
      removeTimeoutAction: function(con, type, diff) {
          var tkey = type+".timeouts."+con.getHalf();
          var timeouts = getContext(tkey);
          setData(tkey, timeouts+diff);
      },
      removeTimeout: function(type, diff, pid, undo) {
        if(!undo && !this.validateTimeoutRemoval(type)) return;

        var con = this; // we have to save the context
        this.waitingForMinute = function() {
          con.removeTimeoutAction(con, type, diff);
          if(!undo) con.addChangeLog("removeTimeout", type, null, diff);
          refreshContext();
        };
        return this.waitingForMinute;
      },
      changeTimeoutsAction: function(con, type, diff, quarter) {
        var tkey = type+".timeouts."+con.getHalf(quarter);
        var timeouts = getContext(tkey);
        setData(tkey, timeouts+diff);
      },
      changeTimeouts: function(type, diff, pid, undo) {
        if(!undo && (!this.validateNextOffense() || !this.validateMatchFinished() || !this.validateTimeouts(type))) return;

        var con = this; // we have to save the context
        this.waitingForMinute = function() {
          var removeTimeout = !undo && con.mayRemoveTimeout(type);
          if(removeTimeout) {
            con.removeTimeout(type, diff, pid, undo)();
          }
          con.changeTimeoutsAction(con, type, diff);
          if(!undo) con.addChangeLog("changeTimeouts", type, null, diff, removeTimeout);
          refreshContext();
        };
      },
      changeConfirmTeamSelectionAction: function(con, type, confirmed) {
        this[type].cdConfirmed = confirmed;
      },
      // diff, pid is needed for undo only! they are never used!
      changeConfirmTeamSelection: function(type, diff, pid, undo) {
        this.changeConfirmTeamSelectionAction(this, type, !undo);
        if(!undo) this.addChangeLog("changeConfirmTeamSelection", type);
        refreshContext();
      },
      changeChallengeAction: function(con, type, diff) {
        var tkey = type+".challengeCount";
        var cc = getContext(tkey) || 0;
        setData(tkey, cc + diff);
      },
      changeChallenge: function(type, diff, pid, undo) {
        if(!undo && (!this.validateNextOffense() || !this.validateMatchFinished() || !this.validateChallenge(type))) return;

        var con = this; // we have to save the context
        this.waitingForMinute = function() {
          con.changeChallengeAction(con, type, diff);
          if(!undo) con.addChangeLog("changeChallenge", type, null, diff);
          refreshContext();
        };
      },
      changeTFoulsAction: function(con, type, diff, pid, quarter, undo) {
          var tkey = type+".fouls.q"+quarter;
          var n = getContext(tkey)+diff;
          setData(tkey, n);

          con.setBonus(type, n >= scoresheet.data.bonus, quarter);

          var pkey = pid+".fouls"
          var foulsCount = getContext(pkey);

          var typekey = pid+".foulTypes";
          if(undo) {
            getContext(typekey)[foulsCount-1] = "";
          } else {
            getContext(typekey)[foulsCount] = "T";
          }

          setData(pkey, foulsCount+diff);

          var ptkey = pid+".tfouls"
          setData(ptkey, getContext(ptkey)+diff);
          con.updatePlayed(pid);
      },
      changeTFouls: function(type, diff, pid, undo) {
        if(!undo && (!this.validateNextOffense() || !this.validateMatchFinished() || !this.validateEligible(pid))) return;  //  || !this.validateFouledOut(pid)
        
        var con = this; // we have to save the context
        this.waitingForMinute = function() {
          con.changeTFoulsAction(con, type, diff, pid, con.quarter, undo);
          if(!undo) { 
            con.addChangeLog("changeTFouls", type, pid, diff);
            con.checkEligibleToContinue(type, pid);
            con.checkCancelledFouls();
            con.validateWithoutCaptain(type);
          }
          refreshContext();
        };
      },
      // The four kinds of the FIBA split, recorded exactly like the two they
      // replace. The counter each one bumps is what decides disqualification:
      // isEjected adds ufouls + tfouls, so the two ejecting kinds feed those and
      // the other two get counters of their own that nothing sums.
      //
      // Mirrors Constant::FOUL_KINDS — the codes must agree, since the printed
      // sheet reads the code this writes into foulTypes.
      specialFoulAction: function(con, type, diff, pid, quarter, undo, fn) {
        var kind = scoresheet.data.foulKinds[fn];

        var tkey = type+".fouls.q"+quarter;
        var n = getContext(tkey)+diff;
        setData(tkey, n);
        con.setBonus(type, n >= scoresheet.data.bonus, quarter);

        var pkey = pid+".fouls";
        var foulsCount = getContext(pkey);

        var typekey = pid+".foulTypes";
        if(undo) {
          getContext(typekey)[foulsCount-1] = "";
        } else {
          getContext(typekey)[foulsCount] = kind.code;
        }

        setData(pkey, foulsCount+diff);

        var ckey = pid+"."+kind.counter;
        setData(ckey, getContext(ckey)+diff);
        con.updatePlayed(pid);
      },
      specialFoul: function(type, diff, pid, undo, fn) {
        if(!undo && (!this.validateNextOffense() || !this.validateMatchFinished() || !this.validateEligible(pid))) return;

        // A technical to a player who ALREADY has five personal fouls. Whose foul it
        // is turns on something only the referees know — whether the substitution had
        // been signalled — so the keeper is asked rather than guessed at:
        //
        //   signalled already  the player was a substitute, so it is the bench's:
        //                      the coach's line, mark B
        //   not yet            he is still a player, so it is his sixth
        //
        // The two roads end in different rows of the sheet, and nothing on the screen
        // could tell them apart, which is why this asks instead of deciding.
        if(!undo && this.isSixthFoulChoice(pid, fn)) {
          this.askSixthFoulTarget(type, diff, pid, fn);
          return;
        }

        this.specialFoulProceed(type, diff, pid, undo, fn);
      },
      // Only a technical, and only once he is out of personal fouls. A disqualifying
      // foul on a fouled-out player is the KZS specification's situation 2, which is
      // deliberately not built — see CLAUDE-TODO.
      isSixthFoulChoice: function(pid, fn) {
        var technicals = ['changeTFouls', 'changeTech1Fouls', 'changeTech2Fouls'];
        return technicals.indexOf(fn) >= 0 && this.isFouledOut(pid);
      },
      askSixthFoulTarget: function(type, diff, pid, fn) {
        var coachIdx = this.coachIndex(type);
        // no coach on the bench: there is nobody to charge it to, so there is no
        // question to ask
        if(coachIdx === null) {
          this.specialFoulProceed(type, diff, pid, false, fn);
          return;
        }

        scoresheet.pendingSixthFoul = { type: type, diff: diff, pid: pid, fn: fn };

        var html = "<p class='crit__what'>" + scoresheet.playerNameWithNumber(pid) +
                   " ima že 5 osebnih napak. Komu se vpiše ta tehnična napaka?</p>" +
                   "<div class='bench-pick'>" +
                   "<button type='button' class='bench-pick__opt js-sixth-target btn-" + type + "' data-target='coach'>" +
                     "<span class='bench-pick__mark'>B</span>" +
                     "<span class='bench-pick__who'>Sodnik je menjavo <b>že pokazal</b></span>" +
                     "<span class='bench-pick__note'>igralec je bil takrat že na klopi — pripiše se trenerju (" +
                       this.altBenchName(type, coachIdx) + ")</span>" +
                   "</button>" +
                   "<button type='button' class='bench-pick__opt js-sixth-target btn-" + type + "' data-target='player'>" +
                     "<span class='bench-pick__mark'>T</span>" +
                     "<span class='bench-pick__who'>Menjave <b>še ni pokazal</b></span>" +
                     "<span class='bench-pick__note'>igralec je bil še v igri — vpiše se kot njegova 6. napaka</span>" +
                   "</button>" +
                   "</div>";

        alerts.warning(html, {
          title: "Šesta napaka",
          modalClass: 'dialog--bench-pick',
          key: 'sixth-foul-' + type
        });
      },
      resolveSixthFoul: function(target) {
        var pending = scoresheet.pendingSixthFoul;
        scoresheet.pendingSixthFoul = null;
        if(!pending) return;

        if(target === 'coach') {
          this.changeBenchTFouls(pending.type, pending.diff, this.coachIndex(pending.type), 'B');
        } else {
          this.specialFoulProceed(pending.type, pending.diff, pending.pid, false, pending.fn);
        }
      },
      specialFoulProceed: function(type, diff, pid, undo, fn) {
        var con = this;
        this.waitingForMinute = function() {
          con.specialFoulAction(con, type, diff, pid, con.quarter, undo, fn);
          if(!undo) {
            con.addChangeLog(fn, type, pid, diff);
            con.checkEligibleToContinue(type, pid);
            con.checkCancelledFouls();
            con.validateWithoutCaptain(type);
          }
          refreshContext();
        };
      },
      changeFlagrantFouls: function(type, diff, pid, undo) { this.specialFoul(type, diff, pid, undo, 'changeFlagrantFouls'); },
      changeDisruptiveFouls: function(type, diff, pid, undo) { this.specialFoul(type, diff, pid, undo, 'changeDisruptiveFouls'); },
      changeTech1Fouls: function(type, diff, pid, undo) { this.specialFoul(type, diff, pid, undo, 'changeTech1Fouls'); },
      changeTech2Fouls: function(type, diff, pid, undo) { this.specialFoul(type, diff, pid, undo, 'changeTech2Fouls'); },
      changeUFoulsAction: function(con, type, diff, pid, quarter, undo) {
          var tkey = type+".fouls.q"+quarter;
          var n = getContext(tkey)+diff;
          setData(tkey, n);

          con.setBonus(type, n >= scoresheet.data.bonus, quarter);

          var pkey = pid+".fouls"
          var foulsCount = getContext(pkey);

          var typekey = pid+".foulTypes";
          if(undo) {
            getContext(typekey)[foulsCount-1] = "";
          } else {
            getContext(typekey)[foulsCount] = "U";
          }

          setData(pkey, foulsCount+diff);

          var ukey = pid+".ufouls"
          setData(ukey, getContext(ukey)+diff);
          con.updatePlayed(pid);
      },
      changeUFouls: function(type, diff, pid, undo) {
        if(!undo && (!this.validateNextOffense() || !this.validateMatchFinished() || !this.validateEligible(pid))) return;  //  || !this.validateFouledOut(pid)

        var con = this; // we have to save the context
        this.waitingForMinute = function() {
          con.changeUFoulsAction(con, type, diff, pid, con.quarter, undo);
          if(!undo) {
            con.addChangeLog("changeUFouls", type, pid, diff);
            con.checkEligibleToContinue(type, pid);
            con.checkCancelledFouls();
            con.validateWithoutCaptain(type);
          }
          refreshContext();
        };
      },
      changeFightsAction: function(con, type, diff, pid, quarter, undo) {
        // Tehnične ali izključujoče napake dosojene zaradi pretepa ne štejejo med napake moštva.

        var pkey = pid+".fouls"
        var foulsCount = getContext(pkey);

        var typekey = pid+".foulTypes";
        if(undo) {
          var foulTypes = getContext(typekey);
          for(let i = 0; i < foulTypes.length; i++) {
            if(foulTypes[i] == 'F') {
              getContext(typekey)[i] = "";
            }
          }
          setData(pkey, foulTypes.filter( t => t != "").length);
        } else {
          if(foulsCount >= 5) {
            getContext(typekey)[foulsCount] = "F";
            setData(pkey, foulsCount+1);
          } else {
            let i = foulsCount;
            while(i < 5) {
              getContext(typekey)[i] = "F";
              i++;
            }
            setData(pkey, 5);
          }
        }

        var fkey = pid+".fights"
        setData(fkey, getContext(fkey)+diff);
      },
      changeFights: function(type, diff, pid, undo) {
        if(!undo && (!this.validateNextOffense() || !this.validateMatchFinished() || !this.validateEligible(pid))) return;  //  || !this.validateFouledOut(pid)

        var con = this; // we have to save the context
        this.waitingForMinute = function() {
          con.changeFightsAction(con, type, diff, pid, con.quarter, undo);
          if(!undo) {
            con.addChangeLog("changeFights", type, pid, diff);
            con.checkEligibleToContinue(type, pid);
            // con.checkCancelledFouls();
            con.validateWithoutCaptain(type);
          }
          refreshContext();
        };
      },
      changeEjectionsAction: function(con, type, diff, pid, quarter, undo) {
        var tkey = type+".fouls.q"+quarter;
        var n = getContext(tkey)+diff;
        setData(tkey, n);

        con.setBonus(type, n >= scoresheet.data.bonus, quarter);

        var pkey = pid+".fouls"
        var foulsCount = getContext(pkey);

        var typekey = pid+".foulTypes";
        if(undo) {
          getContext(typekey)[foulsCount-1] = "";
        } else {
          getContext(typekey)[foulsCount] = "D";
        }

        setData(pkey, getContext(pkey)+diff);

        var ekey = pid+".ejections"
        setData(ekey, getContext(ekey)+diff);
        con.updatePlayed(pid);
      },
      changeEjections: function(type, diff, pid, undo) {
        if(!undo && (!this.validateNextOffense() || !this.validateMatchFinished() || !this.validateEligible(pid))) return;  //  || !this.validateFouledOut(pid)

        var con = this; // we have to save the context
        this.waitingForMinute = function() {
          con.changeEjectionsAction(con, type, diff, pid, con.quarter, undo);
          if(!undo) {
            con.addChangeLog("changeEjections", type, pid, diff);
            con.checkEligibleToContinue(type, pid);
            con.checkCancelledFouls();
            con.validateWithoutCaptain(type);
          }
          refreshContext();
        };
      },
      changeMinute: function(min) {
        setContext("minute", min);
        this.endAction();
      },
      endAction: function() {
        if(this.waitingForMinute) {
          var callbacks = [this.waitingForMinute].flat();
          for(var i = 0; i < callbacks.length; i++) {
            callbacks[i]();
          }
          this.reset();
        }
      },
      changeQuarter: function(diff, undo) {
        if(!this.validateWithoutCaptain('home') || !this.validateWithoutCaptain('away')) {
          this.reset();
          return;
        }

        if(!undo && (!this.validateNextOffense() || !this.validateMatchFinished())) return;

        var con = this;
        var yesCallback = function() {
          // diff = 1 means end quarter
          // diff = -1 means undo

          con.quarter += diff;
          if(con.quarter == 3) {
            con.halfTimePossesion = con.nextOffense;

            if(scoresheet.data.resetScoreEveryQuarter && con.showTeamSelection('home')) {
              alerts.confirmation({ 
                type: 'info',
                title: "Konec polčasa", 
                body: "<p>Pred nadaljevanjem tekme je potrebno določiti igralce za C in D pri obeh ekipah.</p>"
              });
            }
          }
          if(!undo) con.addChangeQuarterLog("changeQuarter", diff);
          setContext("minute", 1);

          // con.matchFinished = !undo && con.isOT() && con.score('home') !== con.score('away');
          // if(!con.matchFinished) {
          //   con.matchFinishedAt = null;
          // }


          if(!undo && con.isOT()) {
            if(scoresheet.data.resetScoreEveryQuarter) {
              con.finishMatch();
            } else {              
              alerts.confirmation({ 
                type: 'info',
                title: "Je tekma končana?", 
                body: "<p>Rezultat: " + con.currentScoreText() + ".</p>",
                yesLabel: "DA, KONEC TEKME",
                noLabel: "NE, SLEDI PODALJŠEK",
                yesCallback: con.finishMatch.bind(con)
              });
            }
          }
          scoresheet.refreshQuarterOptions();
        };
        if(undo) {
          yesCallback();
        } else {
          // The score of the match, not of the quarter about to end: what the keeper
          // is checking against the board is the running total, and quarterScoreText
          // gave only what was scored inside this quarter.
          alerts.confirmation({ title: "Potrditev", body: "<p><b>" + this.endQuarterLabel() + "?</b></p><p>Trenutni rezultat: " + this.currentScoreText() + ".</p>", yesCallback: yesCallback });
        }       
      },
      finishMatch: function(undo) {
        var con = this;
        if(!undo) {
          con.matchFinished = true;
          con.lastPlayedQuarter = con.quarter - 1;
          scoresheet.saveTime();
          con.addFinishMatchLog("finishMatch");
          alerts.success("Konec tekme", "<p>1. sodnik naj pregleda zapisnik. Popravke narediš v Dogodkih.</p><p>Kapetana obeh ekip lahko vložita pritožbo.</p><p>Po podpisih vseh sodnikov pojdi na poročilo o tekmi.</p>");
        } else {
          con.matchFinished = false;
          con.matchFinishedAt = null;
          con.lastPlayedQuarter = null;
        }
      },
      changeBenchTFoulsUndo: function(type, diff, coachIndex, newCoachIndex, newOtherIndex, mark) {
        var con = this; // we have to save the context
        this.waitingForMinute = function() {
          // 1. add to coach's fouls (diff is negative!)
          var coach = con.bench(type, coachIndex);
          var key = con.benchCounterFor(mark);
          coach[key] += diff;
          var foulTypesIndex = con.benchFouls(type, coachIndex);

          // 2. fix mark
          coach.foulTypes[foulTypesIndex] = "";

          // 3. if this was an ejecting foul, then we must set up coach and staff back appropriately
          if(newCoachIndex !== null) {
            con.setCoach(coach);

            var exCoach = con.bench(type, newCoachIndex);
            var exOther = con.bench(type, newOtherIndex);
            con.setAssistantAndOtherRoles(exCoach, exOther);
          }
        };
      },
      changeBenchTFoulsAction: function(con, type, diff, coachIndex, mark) {
        // 1. add to coach's fouls
        var coach = con.bench(type, coachIndex);
        var key = con.benchCounterFor(mark);
        var foulTypesIndex = con.benchFouls(type, coachIndex);
        coach[key] += diff;

        // 2. add appropriate mark
        coach.foulTypes[foulTypesIndex] = mark;

        // 3. if this was an ejection foul for coach, coach must be relased of the duties, and new one assigned; same for staff!      
        var newCoachIndex = null;
        var newOtherIndex = null;
        if(con.isBenchEjected(type, coachIndex)) {
          // with the new foul coach is ejected!
          con.setRoleNull(coach);

          newCoachIndex = con.nextEligibleBenchIndex(type,coachIndex+1);
          var newCoach = con.bench(type, newCoachIndex);
          con.setCoach(newCoach, con.isOriginallyOther(newCoach), type);

          newOtherIndex = con.nextEligibleBenchIndex(type,newCoachIndex+1);
          var newOther = con.bench(type, newOtherIndex);
          con.setOther(newOther);
        }
        return { newCoachIndex: newCoachIndex, newOtherIndex: newOtherIndex };
      },
      changeBenchTFoulsCallback: function(type, diff, coachIndex, mark, undoPrevious) {
        var con = this; // we have to save the context
        return function() {
          // 1., 2., 3. handle data
          var d = con.changeBenchTFoulsAction(con, type, diff, coachIndex, mark);

          // 4. add to log
          con.addChangeBenchLog("changeBenchTFouls", type, diff, coachIndex, d.newCoachIndex, d.newOtherIndex, mark, scoresheet.data.COACH, undoPrevious);

          // 5. throw alert if necessary — newCoachIndex is set here only when this
          // foul was the coach's last, so it always names a successor
          con.checkBenchEligibleToContinue(type, coachIndex, d.newCoachIndex);

          // 6. check if technical might be cancelled with another one on the other side
          if(!undoPrevious) con.checkCancelledFouls();

          // 7. check if team is without captain
          con.validateWithoutCaptain(type);
        };
      },
      changeBenchTFouls: function(type, diff, coachIndex, mark) {    // mark: C or B; always coach! even if staff gets technical!
        if(!this.validateNextOffense() || !this.validateMatchFinished() || !this.validateBenchEligible(type, coachIndex)) return;
        this.waitingForMinute = this.changeBenchTFoulsCallback(type, diff, coachIndex, mark);
      },
      changeBenchEjectionsUndo: function(type, diff, benchIndex, newBenchIndex, newNewBenchIndex, mark) {
        var con = this; // we have to save the context
        this.waitingForMinute = function() {
          // 1. add to coach's fouls (diff is negative!)
          var bench = con.bench(type, benchIndex);
          var key = "ejections";
          bench[key] += diff;
          var foulTypesIndex = con.benchFouls(type, benchIndex);

          // 2. fix mark
          bench.foulTypes[foulTypesIndex] = "";

          if(con.isPreviousOther(bench)) {
            con.setOther(bench);

            var exOther = con.bench(type, newBenchIndex);
            con.setRoleNull(exOther);
          } else if(con.isPreviousAssistant(bench)) {
            con.setAssistant(bench);
          } else {
            con.setCoach(bench);

            var exCoach = con.bench(type, newBenchIndex);
            var exOther = con.bench(type, newNewBenchIndex);
            con.setAssistantAndOtherRoles(exCoach, exOther);
          }
        };
      },
      changeBenchEjectionsAction: function(con, type, diff, benchIndex) {
        // 1. change ejections
        var bench = con.bench(type, benchIndex);
        var key = "ejections";
        var foulTypesIndex = con.benchFouls(type, benchIndex);
        bench[key] += diff;

        // 2. add appropriate mark
        bench.foulTypes[foulTypesIndex] = "D";
        var role = bench.role;

        // 3. change roles
        var newCoachIndex = null;
        var newOtherIndex = null;

        if(con.isCoach(type, benchIndex)) {
          con.setRoleNull(bench);
          newCoachIndex = con.nextEligibleBenchIndex(type, benchIndex+1);
          var newCoach = con.bench(type, newCoachIndex);
          con.setCoach(newCoach, con.isOriginallyOther(newCoach), type);
          
          // here new other may be the same as current other
          newOtherIndex = con.nextEligibleBenchIndex(type, newCoachIndex+1);
          var newOther = con.bench(type, newOtherIndex);
          con.setOther(newOther);
        } else if(con.isOther(bench)) {
          con.setRoleNull(bench);
          newCoachIndex = con.nextEligibleBenchIndex(type, benchIndex+1);
          var newOther = con.bench(type, newCoachIndex);
          con.setOther(newOther);
        } else if(con.isAssistant(bench)) {
          con.setRoleNull(bench);
        }
        return { newCoachIndex: newCoachIndex, newOtherIndex: newOtherIndex, role: role };
      },
      changeBenchEjections: function(type, diff, benchIndex) {   // stype = coach
        if(!this.validateNextOffense() || !this.validateMatchFinished() || !this.validateBenchEligible(type, benchIndex)) return;

        var con = this; // we have to save the context
        this.waitingForMinute = [function() {
          var d = con.changeBenchEjectionsAction(con, type, diff, benchIndex);

          con.addChangeBenchLog("changeBenchEjections", type, diff, benchIndex, d.newCoachIndex, d.newOtherIndex, "D", d.role);
          // newCoachIndex carries the new OTHER when an "other" was the one ejected,
          // so the previous role is what decides whether the bench changed hands
          con.checkBenchEligibleToContinue(type, benchIndex,
            d.role === scoresheet.data.COACH ? d.newCoachIndex : null);

          con.checkCancelledFouls();
          con.validateWithoutCaptain(type);
        }];

        if(!con.isCoach(type, benchIndex)) {
          // Somebody else's ejection is charged to the coach. It used to be written
          // as a plain B, which said "a technical happened on the bench" and lost the
          // fact that a person had been sent off; BD says both, and counts toward the
          // coach's disqualification exactly as the B did.
          var coachIndex = this.coachIndex(type);
          this.waitingForMinute.push( this.changeBenchTFoulsCallback(type, 1, coachIndex, "BD", true) );
        }
      },
      changeStarting5Action: function(pid, starting5) {
        var key = pid+".isStarting";
        setData(key, starting5);
        this.updatePlayed(pid);
      },
      changeStarting5: function(pid, starting5, undo) {
        this.changeStarting5Action(pid, starting5);
        if(!undo) this.addChangeStarting5Log(pid, starting5);
        refreshContext();
      },
      changePlayedAction: function(pid) {
        var key = pid+".playedSelected";
        setData(key, !getContext(key))
        this.updatePlayed(pid);
      },
      changePlayed: function(pid, undo) {
        if(!this.validateWithoutCaptain('home') || !this.validateWithoutCaptain('away')) {
          this.reset();
          return;
        }
        if(!undo && this.hasPlayed(pid)) return;
        if(!undo && (!this.validateMatchFinished() || !this.validateEligible(pid) || !this.validateFouledOut(pid))) return;

        this.changePlayedAction(pid);

        if(!undo) this.addChangePlayedLog(pid);
        refreshContext();
      },
      setState: function(newState) {
        this.previousPreviousState = this.previousState;
        this.previousState = this.state;
        setContext("state", newState);
      },
      undoSetState: function() {
        this.state = this.previousState;
        this.previousState = this.previousPreviousState;
        setContext("previousPreviousState", null);
      },
      getHalf: function(quarter) {
        quarter = quarter || this.quarter;
        if(quarter <= 2) {
          return "h1";
        } else if(quarter <= 4) {
          return "h2";
        } else {
          return "q" + quarter;
        }
      },
      isTimeoutAvailable: function(type, i) {
        return i <= this.getTotalTimeouts() && !this.isTimeoutTaken(type, i);
      },
      isTimeoutTaken: function(type, i) {
        return this[type].timeouts[this.getHalf()] >= i;
      },
      getTotalTimeouts: function() {
        return scoresheet.data.timeouts[this.getHalf()];
      },
      getTimeoutsLeft: function(type) {
        return this.getTotalTimeouts() - this[type].timeouts[this.getHalf()];
      },
      getChallengesLeft: function(type) {
        return scoresheet.data.challengeCount - (this[type].challengeCount || 0);
      },
      selectChallenge: function(type) {
        if(!this.validateWithoutCaptain('home') || !this.validateWithoutCaptain('away')) {
          this.reset();
          return;
        }
        
        this.reset();
        this.pendingTeamType = type; // display only: the composer names the team
        this.setState(scoresheet.data.state.CHALLENGE_TAKEN);
        this.changeChallenge(type, 1);        
      },
      selectTimeout: function(type) {
        if(!this.validateWithoutCaptain('home') || !this.validateWithoutCaptain('away')) {
          this.reset();
          return;
        }

        this.reset();
        this.setState(scoresheet.data.state.TIMEOUT_TAKEN);
        this.pendingTeamType = type; // display only: the composer names the team
        this.changeTimeouts(type, 1);
      },
      selectTimeoutRemoval: function(type) {
        if(!this.validateWithoutCaptain('home') || !this.validateWithoutCaptain('away')) {
          this.reset();
          return;
        }

        this.reset();
        this.setState(scoresheet.data.state.REMOVE_TIMEOUT);
        this.pendingTeamType = type; // display only: the composer names the team
        this.removeTimeout(type, 1);
      },
      // One button per foul, and it asks which kind.
      //
      // A personal foul has always been two questions in one — is it with free
      // throws — and FIBA has since split nešportna into groba/taktična and
      // tehnična into kat. 1/kat. 2. Spelled out, the rail would carry eight
      // buttons of which two pairs look alike to somebody working at speed. So
      // each foul keeps one button, and the dialog asks the one thing that
      // distinguishes the halves — which, for the new kinds, is whether it counts
      // toward disqualification and is circled on the sheet.
      //
      // Nothing is recorded by the question: closing it leaves the sheet as it was.
      chooseFoulKind: function(which) {
        var con = this;

        // The bench has one technical, not two categories. If the keeper has already
        // said this is about the bench, the category question has no answer that
        // means anything — so skip it and ask the question that does: who was it.
        // TFOUL_PRESSED is the plain technical the bench has always recorded; the
        // popup's choice, not the category, decides the mark.
        if(which === 'technical' && this.isBenchEntityState(this.state) && this.ttype !== null) {
          this.setState(scoresheet.data.state.TFOUL_PRESSED);
          this.chooseBenchTarget(this.ttype, 'technical');
          return;
        }

        var kinds = {
          personal: {
            title: 'Osebna napaka',
            question: 'Ali sledijo prosti meti?',
            why: null,
            yes: { label: 'S prostimi meti', state: 'FTFOUL_PRESSED' },
            no:  { label: 'BREZ prostih',    state: 'FOUL_PRESSED' }
          },
          unsportsmanlike: {
            // The same words as the button that opens it. "Nešportna osebna
            // napaka" is the family, and it is also what the sheet still prints
            // for fouls recorded before the FIBA split — while this dialog only
            // ever writes one of the two below.
            title: 'Taktična / groba napaka',
            question: 'Katera napaka je bila dosojena?',
            why: "<b>Groba</b> šteje k izključitvi in se na zapisniku obkroži.<br>"
               + "<b>Taktična</b> ne šteje k izključitvi.",
            yes: { label: 'Groba',    state: 'FLAGRANT_PRESSED' },
            no:  { label: 'Taktična', state: 'DISRUPTIVE_PRESSED' }
          },
          technical: {
            title: 'Tehnična napaka',
            question: 'Katera napaka je bila dosojena?',
            why: "<b>Kategorija 1</b> šteje k izključitvi in se na zapisniku obkroži.<br>"
               + "<b>Kategorija 2</b> ne šteje k izključitvi.",
            yes: { label: 'Kategorija 1', state: 'TECH1_PRESSED' },
            no:  { label: 'Kategorija 2', state: 'TECH2_PRESSED' }
          }
        }[which];
        if(!kinds) return;

        var body = "<p class='foul-kinds__q'>" + kinds.question + "</p>";
        if(kinds.why) body += "<p class='foul-kinds__why'>" + kinds.why + "</p>";

        alerts.confirmation({
          type: 'warning',
          title: kinds.title,
          modalClass: 'dialog--foul-kind',
          body: body,
          yesLabel: kinds.yes.label,
          noLabel: kinds.no.label,
          yesCallback: function() { con.selectAction(scoresheet.data.state[kinds.yes.state]); },
          noCallback: function() { con.selectAction(scoresheet.data.state[kinds.no.state]); }
        });
      },
      selectAction: function(newState) {
        if(!this.validateWithoutCaptain('home') || !this.validateWithoutCaptain('away')) {
          this.reset();
          return;
        }

        this.setState(newState);
        if(this.previousState === scoresheet.data.state.PLAYER_SELECTED && this.playerId !== null) {
          var pid = this.playerId;
          var type = scoresheet.player(pid).type;
          if(newState === scoresheet.data.state.FT_PRESSED) {
            this.handleFreeThrow(type, pid);
            // this.changeScore(type, 1, pid);
          } else if(newState == scoresheet.data.state.PT2_PRESSED) {
            this.changeScore(type, 2, pid);
          } else if(newState == scoresheet.data.state.PT3_PRESSED) {
            this.changeScore(type, 3, pid);
          } else if(newState == scoresheet.data.state.FOUL_PRESSED) {
            this.changeFouls(type, 1, pid);
          } else if(newState == scoresheet.data.state.FTFOUL_PRESSED) {
            this.changeFTFouls(type, 1, pid);
          } else if(newState == scoresheet.data.state.TFOUL_PRESSED) {
            this.changeTFouls(type, 1, pid);
          } else if(newState == scoresheet.data.state.UFOUL_PRESSED) {
            this.changeUFouls(type, 1, pid);
          } else if(newState == scoresheet.data.state.FLAGRANT_PRESSED) {
            this.changeFlagrantFouls(type, 1, pid);
          } else if(newState == scoresheet.data.state.DISRUPTIVE_PRESSED) {
            this.changeDisruptiveFouls(type, 1, pid);
          } else if(newState == scoresheet.data.state.TECH1_PRESSED) {
            this.changeTech1Fouls(type, 1, pid);
          } else if(newState == scoresheet.data.state.TECH2_PRESSED) {
            this.changeTech2Fouls(type, 1, pid);
          } else if(newState == scoresheet.data.state.EJECTION_PRESSED) {
            this.changeEjections(type, 1, pid);
          } else if(newState == scoresheet.data.state.FIGHT_PRESSED) {
            this.changeFights(type, 1, pid);
          }
        } else if([scoresheet.data.state.COACH_SELECTED, scoresheet.data.state.ASSISTANT_SELECTED, scoresheet.data.state.OTHER_SELECTED].indexOf(this.previousState) >= 0 && this.ttype !== null) {
          // Bench selected first, verb second. Who it was is still an open question —
          // the button is the whole bench — so it is asked the same way round.
          // Players only: the bench keeps the one technical it always had, so the
          // category the keeper picked is simply not used here.
          if(this.isBenchTechnicalState(newState)) {
            this.chooseBenchTarget(this.ttype, 'technical');
          } else if(newState == scoresheet.data.state.EJECTION_PRESSED) {
            this.chooseBenchTarget(this.ttype, 'ejection');
          } else {
            alerts.warning("<p>Edina možna dogodka sta: tehnična napaka ali izključitev!</p>");
            this.undoSetState()
          }
        } else {
          this.playerId = null;
          this.ttype = null;
          this.selectedBenchIndex = null;
          refreshContext();
        }
      },
      selectPlayer: function(type, pid) {
        if(this.state === scoresheet.data.state.CHANGE_CAPTAIN) {
          if(type != this.ttype) {
            alerts.warning("<p>Izberi igralca " + (this.ttype == 'home' ? 'domače' : 'gostujoče') + " ekipe!</p>");
          } else {
            this.changeCaptain(type, pid);
          }
        } else {
          if(!this.validateWithoutCaptain('home') || !this.validateWithoutCaptain('away')) {
            this.reset();
            return;
          }

          this.selectedBenchIndex = null;
          setContext("playerId", pid);
          // verb-first order: after this setState the verb survives only inside the
          // waitingForMinute closure, so its NAME is remembered here for the slots
          if(this.isActionState(this.state)) setContext("pendingActionState", this.state);
          this.setState(scoresheet.data.state.PLAYER_SELECTED);
          
          if(this.previousState == scoresheet.data.state.FT_PRESSED) {
            this.handleFreeThrow(type, pid);
          } else if(this.previousState == scoresheet.data.state.PT2_PRESSED) {
            this.changeScore(type, 2, pid);
          } else if(this.previousState == scoresheet.data.state.PT3_PRESSED) {
            this.changeScore(type, 3, pid);
          } else if(this.previousState == scoresheet.data.state.FOUL_PRESSED) {
            this.changeFouls(type, 1, pid);
          } else if(this.previousState == scoresheet.data.state.FTFOUL_PRESSED) {
            this.changeFTFouls(type, 1, pid);
          } else if(this.previousState == scoresheet.data.state.TFOUL_PRESSED) {
            this.changeTFouls(type, 1, pid);
          } else if(this.previousState == scoresheet.data.state.UFOUL_PRESSED) {
            this.changeUFouls(type, 1, pid);
          } else if(this.previousState == scoresheet.data.state.FLAGRANT_PRESSED) {
            this.changeFlagrantFouls(type, 1, pid);
          } else if(this.previousState == scoresheet.data.state.DISRUPTIVE_PRESSED) {
            this.changeDisruptiveFouls(type, 1, pid);
          } else if(this.previousState == scoresheet.data.state.TECH1_PRESSED) {
            this.changeTech1Fouls(type, 1, pid);
          } else if(this.previousState == scoresheet.data.state.TECH2_PRESSED) {
            this.changeTech2Fouls(type, 1, pid);
          } else if(this.previousState == scoresheet.data.state.EJECTION_PRESSED) {
            this.changeEjections(type, 1, pid);
          } else if(this.previousState == scoresheet.data.state.FIGHT_PRESSED) {
            this.changeFights(type, 1, pid);
          }
        }
      },
      // The Klop button: the bench arrives as ONE thing, and who it was is asked
      // afterwards, in the popup, where the four possibilities can be spelled out.
      //
      // The 19 cards this replaces existed because the data addresses bench people by
      // array index — 17 of them anonymous spares the promotion chain needs. That is
      // still true underneath; it just stopped being the keeper's problem.
      selectBenchButton: function(type) {
        if(!this.validateWithoutCaptain('home') || !this.validateWithoutCaptain('away')) {
          this.reset();
          return;
        }
        if(!this.hasCoach(type)) {
          alerts.warning("<p>Ekipa nima vpisanega trenerja, zato na klop ni mogoče vpisati ničesar.</p>");
          return;
        }

        if(this.isBenchTechnicalState(this.state)) {
          this.chooseBenchTarget(type, 'technical');
        } else if(this.state === scoresheet.data.state.EJECTION_PRESSED) {
          this.chooseBenchTarget(type, 'ejection');
        } else {
          // no verb yet, or one the bench cannot take: select the bench and let
          // selectBenchPerson say so, exactly as pressing a card used to
          this.selectBenchPerson(type, this.coachIndex(type));
        }
      },
      // The technical states are three because of the player-side FIBA split; the
      // bench keeps the one technical it always had, so all three land here.
      isBenchEntityState: function(state) {
        return [scoresheet.data.state.COACH_SELECTED,
                scoresheet.data.state.ASSISTANT_SELECTED,
                scoresheet.data.state.OTHER_SELECTED].indexOf(state) >= 0;
      },
      isBenchTechnicalState: function(state) {
        return [scoresheet.data.state.TFOUL_PRESSED,
                scoresheet.data.state.TECH1_PRESSED,
                scoresheet.data.state.TECH2_PRESSED].indexOf(state) >= 0;
      },
      // Who can be given a technical: the coach, or anybody else on the bench. The
      // rule does not care which of the others it was — the mark goes on the coach's
      // line either way — so this is deliberately two options and not a roster.
      benchTechnicalTargets: function(type) {
        var out = [];
        var ci = this.coachIndex(type);
        var oi = this.benchIndex(type, scoresheet.data.OTHER);
        if(ci !== null) {
          out.push({ index: ci, who: 'Trener', name: this.benchName(type, ci, true, true),
                     note: 'napaka trenerja', mark: 'C' });
        }
        if(oi !== null) {
          out.push({ index: oi, who: 'Druga oseba na klopi', name: '',
                     note: 'pripiše se trenerju', mark: 'B' });
        }
        return out;
      },
      // Who can be sent off. Here the rule DOES care: the coach's own ejection hands
      // the bench to somebody else, while anyone else's is charged to the coach as BD.
      benchEjectionTargets: function(type) {
        var out = [];
        var ci = this.coachIndex(type);
        var ai = this.benchIndex(type, scoresheet.data.ASSISTANT);
        var oi = this.benchIndex(type, scoresheet.data.OTHER);
        if(ci !== null) {
          out.push({ index: ci, who: 'Trener', name: this.benchName(type, ci, true, true),
                     note: 'vodenje prevzame naslednji', mark: 'D' });
        }
        if(ai !== null) {
          out.push({ index: ai, who: 'Pomočnik trenerja', name: this.benchName(type, ai, true, true),
                     note: 'trenerju se pripiše BD', mark: 'D' });
        }
        if(oi !== null) {
          out.push({ index: oi, who: 'Druga oseba na klopi', name: '',
                     note: 'trenerju se pripiše BD', mark: 'D' });
        }
        return out;
      },
      chooseBenchTarget: function(type, kind) {
        var targets = kind === 'ejection' ? this.benchEjectionTargets(type) : this.benchTechnicalTargets(type);
        if(targets.length === 0) {
          alerts.warning("<p>Na klopi ni nikogar, ki bi mu bilo mogoče vpisati dogodek.</p>");
          return;
        }

        var html = "<div class='bench-pick'>";
        for(var t of targets) {
          html += "<button type='button' class='bench-pick__opt js-bench-target btn-" + type + "'" +
                  " data-type='" + type + "' data-index='" + t.index + "'>" +
                  "<span class='bench-pick__mark'>" + t.mark + "</span>" +
                  "<span class='bench-pick__who'>" + t.who +
                  (t.name ? " <b>" + t.name + "</b>" : "") + "</span>" +
                  "<span class='bench-pick__note'>" + t.note + "</span>" +
                  "</button>";
        }
        html += "</div>";

        alerts.warning(html, {
          title: kind === 'ejection' ? "Izključitev s klopi" : "Tehnična napaka na klopi",
          modalClass: 'dialog--bench-pick',
          key: 'bench-pick-' + type
        });
      },
      selectBenchPerson: function(type, i) {
        if(!this.validateWithoutCaptain('home') || !this.validateWithoutCaptain('away')) {
          this.reset();
          return;
        }

        this.playerId = null;
        this.selectedBenchIndex = i;
        setContext("ttype", type);
        // same annotation as selectPlayer: the verb's name for the slots
        if(this.isActionState(this.state)) setContext("pendingActionState", this.state);
        var isCoach = this.isCoach(type, i);
        if(isCoach) {
          this.setState(scoresheet.data.state.COACH_SELECTED);
        } else if(this.isBenchAssistant(type, i)) {
          this.setState(scoresheet.data.state.ASSISTANT_SELECTED);
        } else if(this.isBenchOther(type, i)) {
          this.setState(scoresheet.data.state.OTHER_SELECTED);
        }
        
        if(this.isBenchTechnicalState(this.previousState)) {
          this.changeBenchTFouls(type, 1, this.coachIndex(type), isCoach ? 'C' : 'B');
        } else if(this.previousState == scoresheet.data.state.EJECTION_PRESSED) {
          this.changeBenchEjections(this.ttype, 1, this.selectedBenchIndex);
        } else if(this.isActionState(this.previousState)) {
          alerts.warning("<p>Edina možna dogodka sta: tehnična napaka ali izključitev!</p>");
          this.undoSetState();
        }
      },
      isFirstOffenseSet: function() {
        return this.firstOffense !== null;
      },
      setFirstOffenseAction: function(type, undo) {
        setData("nextOffense", this.otherType(type));
        setData("firstOffense", type);
      },
      setFirstOffense: function(type, undo) {
        this.setFirstOffenseAction(type);
        this.reset();
        if(!undo) this.addFirstOffenseLog(type);
      },
      hasNextOffense: function(direction) {
        return this[direction] == this.nextOffense;
      },
      otherType: function(type) {
        if(type) {
          return type == 'home' ? 'away' : 'home';
        } else {
          return null;
        }
      },
      changeOffenseAction: function() {
        var newType = this.otherType( this.nextOffense );
        setData("nextOffense", newType);
        return newType;
      },
      changeOffense: function(undo) {
        if(!undo && !this.validateMatchFinished()) return;
        var newType = this.changeOffenseAction();
        this.reset();
        if(!undo) this.addNextOffenseLog(newType);
      },
      showSide: function(side, type) {
        return this[side] === type;
      },
      showPlayersAndStaff: function(type) {
        return !this.showTeamSelection(type);
      },
      showTeamSelection: function(type) {
        return scoresheet.data.resetScoreEveryQuarter && !this[type].cdConfirmed && this.quarter >= 3;
      },
      numberOfPlayersInC: function(type) {
        return this.numberOfPlayerForField(type, 'in_c');
      },
      numberOfPlayersInD: function(type) {
        return this.numberOfPlayerForField(type, 'in_d');
      },
      mayConfirmTeamSelection: function(type) {
        return this.numberOfPlayersInC(type) >= 4 && this.numberOfPlayersInD(type) >= 4;
      },
      numberOfPlayerForField(type, fn) {
        var n = 0;
        for(var pid of (Object.keys(scoresheet.data.playerId2data) || []) ) {
          var p = this[pid] || {};
          if(scoresheet.playerType(pid) == type && p[fn]) {
            n++;
          }
        }
        return n;
      },
      reset: function() {
        this.selectedBenchIndex = null;
        this.ttype = null;
        this.playerId = null;
        this.waitingForMinute = null;
        this.pendingActionState = null;
        this.pendingTeamType = null;
        this.previousState = null;
        this.state = null;
        refreshContext();

        for(type of ['home', 'away']) {
          if(!this.validateWithoutCaptain(type, true)) {
            this.ttype = type;
            this.setState(scoresheet.data.state.CHANGE_CAPTAIN);
          }
        }
      },
      // Has the browser's copy pulled ahead of the server's, i.e. are there events
      // the server has not got. 30s, not the previous 5 minutes: at one save per
      // action, five minutes of silence can hide a whole quarter's worth of events,
      // and the keeper is told only once it is far too late to do anything about it.
      // No false positives from a quiet stretch — neither timestamp moves unless a
      // save is attempted, so the gap only grows when saves are actually failing.
      showInternetProblemError: function() {
        let obj = scoresheet.getFromLocalStorage();
        return !this.saving && obj && obj.scoresheetObj && obj.scoresheetObj.localStorageSaveTime && obj.scoresheetObj.serverSaveTime && obj.scoresheetObj.localStorageSaveTime - obj.scoresheetObj.serverSaveTime > 30*1000;
      },
      updateColors: function() {
        scoresheet.updateColors(this.home.color, this.away.color);
      },
      toggleShowPlayerNames: function() {
        setContext('showPlayerNames', this.showPlayerNames === false || this.showPlayerNames === 'false');
      },
      switchSides: function() {
        var exLeft = this.left;
        this.left = this.right;
        setContext("right", exLeft);
        scoresheet.save();
      },
      shareScoresheet: function() {
        var yesCallback = function() {
          scoresheet.invite( $('#share_email').val() );
        };
        alerts.confirmation({ type: 'info', title: 'Deli povezavo do zapisnika', body: '<p>V spodnje polje vpišite elektronski naslov osebe, komur želite poslati povezavo za spremljanje digitalnega zapisnika v živo.</p><p><input class="form-control" type="email" name="share_email" id="share_email" value="" placeholder="E-mail" /></p>', yesLabel: 'Pošlji', noLabel: 'Prekliči', yesCallback: yesCallback });
      },
      toggleFullscreen: function(forceFullScreen) {
        if(this.getFullscreenElement()) {
           document.exitFullscreen();
           setContext('fullScreen', false);
        } else {
          document.documentElement.requestFullscreen().catch(console.log);
          setContext('fullScreen', true);
        }
      },
      getFullscreenElement: function() {
        return document.fullscreenElement   //standard property
          || document.webkitFullscreenElement //safari/opera support
          || document.mozFullscreenElement    //firefox support
          || document.msFullscreenElement;    //ie/edge support
      },
      updatePlayed: function(pid, refresh) {
        var p = this[pid];
        setData(pid+".played", p.playedSelected || p.isStarting || p.cft > 0 || p.mft > 0 || p.ft > 0 || p.pt2 > 0 || p.pt3 > 0 || p.fouls > 0);
        if(refresh) {
          refreshContext();    
        }
      },
      save: function() {
        scoresheet.save();
      },
      forceSave: function() {
        this.rebuildFromLogs();
        scoresheet.save();
      },
      maySave: function() {
        return true;
        // let obj = scoresheet.getFromLocalStorage();
        // return obj && obj.scoresheetObj && obj.scoresheetObj.localStorageSaveTime && obj.scoresheetObj.serverSaveTime && obj.scoresheetObj.localStorageSaveTime - obj.scoresheetObj.serverSaveTime > 60*1000;
      },
      rebuildFromLogs: function() {
        scoresheet.setDefaults();
        for(log of this.logs) {
          if(log.editedById) continue;

          switch(log.fn) {
            case "changeStarting5":
              this.changeStarting5Action(log.pid, log.starting5);
              break;
            case "changeCaptain":
              this.changeCaptainAction(log.type, log.pid);
              break;
            case "setFirstOffense":
              this.setFirstOffenseAction(log.type);
              break;
            case "changeOffense":
              this.changeOffenseAction();
              break;
            case "changePlayed":
              this.changePlayedAction(log.pid);
              break;
            case "changeFouls":
              this.changeFoulsAction(this, log.type, log.diff, log.pid, log.quarter);
              break;
            case "changeFTFouls":
              this.changeFTFoulsAction(this, log.type, log.diff, log.pid, log.quarter);
              break;
            case "changeScore":
              this.changeScoreAction(this, log.type, log.diff, log.pid, log.quarter);
              break;
            case "changeTFouls":
              this.changeTFoulsAction(this, log.type, log.diff, log.pid, log.quarter);
              break;
            case "changeUFouls":
              this.changeUFoulsAction(this, log.type, log.diff, log.pid, log.quarter);
              break;
            case "changeFlagrantFouls":
            case "changeDisruptiveFouls":
            case "changeTech1Fouls":
            case "changeTech2Fouls":
              this.specialFoulAction(this, log.type, log.diff, log.pid, log.quarter, false, log.fn);
              break;
            case "changeEjections":
              this.changeEjectionsAction(this, log.type, log.diff, log.pid, log.quarter);
              break;
            case "changeFights":
              this.changeFightsAction(this, log.type, log.diff, log.pid, log.quarter);
              break;
            case "removeTimeout":
              this.removeTimeoutAction(this, log.type, log.diff);
              break;
            case "changeTimeouts":
              this.changeTimeoutsAction(this, log.type, log.diff, log.quarter);
              break;
            case "changeConfirmTeamSelection":
              this.changeConfirmTeamSelectionAction(this, log.type, true);
              break;
            case "changeChallenge":
              this.changeChallengeAction(this, log.type, log.diff);
              break;
            case "cancelFt":
              this.cancelFtAction(this, log.type, log.diff, log.pid);
              break;
            case "changeQuarter":
              break;
            case "changeBenchTFouls":
              this.changeBenchTFoulsAction(this, log.type, log.diff, log.coachIndex, log.mark);
              break;
            case "changeBenchEjections":
              this.changeBenchEjectionsAction(this, log.type, log.diff, log.coachIndex);
              break;
            case "finishMatch":
              break;
          }
        }
        refreshContext();
      }
    }, scoresheetObj, { state: null, playerId: null, fullScreen: false });

    // The player-coach link, applied on EVERY load rather than only when the bench
    // is built. A stored scoresheet overrides the fresh bench in the extend above,
    // so a link made at prematch after the live sheet had already saved once would
    // otherwise never arrive — and the ejection rule would quietly count one of his
    // two accounts.
    //
    // Only the row that started as coach, and only when the server has a link:
    // setCoach stamps a pid of its own when a captain takes the bench mid-match,
    // and that must not be overwritten or cleared.
    for(var t of ['home', 'away']) {
      var linked = (side2type2data[t] && side2type2data[t][scoresheet.data.COACH] || {}).playerFmsId;
      if(!linked) continue;

      var benchRows = context[t].bench || [];
      for(var bi = 0; bi < benchRows.length; bi++) {
        if(benchRows[bi] && benchRows[bi].originalRole === scoresheet.data.COACH) {
          benchRows[bi].pid = String(linked);
          break;
        }
      }
    }

    // UPDATE COLORS AND PLAYED!
    context.updateColors();
    var shouldAddStarters = context.logs.length == 0;
    var sortedPlayers = scoresheet.sortByTypeAndNumber(scoresheet.data.playerId2data);
    for(pdata of sortedPlayers) {
      var pid = pdata.id;
      if(shouldAddStarters && pdata.is_starting) {
        context[pid].isStarting = true;
        context.addChangeStarting5Log(pid, true);
      }
      context.updatePlayed(pid);
    }

    // COLOR PICKER!
    $('select.colorpicker').simplecolorpicker({picker: true, theme: 'fontawesome'}).on('change', function() {
      var value = $(this).val();
      setContext($(this).data('type') + ".color." + $(this).data('colortype'), value);

      scoresheet.save();
      context.updateColors();
    });
    refreshContext();

    // BUILD FILTERS!
    scoresheet.buildFilters();

    // KEYS! TODO
    // The captain list inside the captainless dialog. Delegated, because the dialog
    // is built by alerts.js after Twine has bound the page — its markup carries no
    // bindings and its id changes every time.
    $(document).on('click', '.js-pick-captain', function() {
      var $opt = $(this);
      context.changeCaptain($opt.data('type'), $opt.data('pid'));
      $opt.closest('.modal').modal('hide');
    });

    // Who on the bench the technical or the ejection was for. Same reason for the
    // delegation as above, and the same order: act first, then close, so a warning
    // the entry raises queues behind this dialog instead of racing it.
    $(document).on('click', '.js-bench-target', function() {
      var $opt = $(this);
      context.selectBenchPerson($opt.data('type'), $opt.data('index'));
      $opt.closest('.modal').modal('hide');
    });

    $(document).on('click', '.js-sixth-target', function() {
      var $opt = $(this);
      context.resolveSixthFoul($opt.data('target'));
      $opt.closest('.modal').modal('hide');
    });

    // Shortcuts for the keeper's hands on the sheet — and ONLY there.
    //
    // This listens on document and used to fire wherever the caret was. Typing an
    // Opomba in Uredi dogodek or Vrini dogodek and pressing Ctrl+Z did not undo the
    // typing: it ran context.undo() and took the last entry off the match. Esc threw
    // away the action in progress instead of closing the dialog, and space with a
    // button focused clicked the button AND confirmed the event behind it. All three
    // silently, mid-match.
    //
    // Two gates. A field is anywhere a caret can be, contenteditable included, and a
    // disabled or readonly input still counts — the point is where the user believes
    // they are typing. An open modal is its own conversation: whatever is on the sheet
    // underneath is not what these keys are for, and Bootstrap already closes a modal
    // on Esc itself.
    function typingInAField(el) {
      if(!el) return false;
      if(el.isContentEditable) return true;
      return ['INPUT', 'TEXTAREA', 'SELECT'].indexOf(el.tagName) >= 0;
    }

    // Space is NOT gated on the focused element, and that is deliberate. It looks like
    // it should be: the browser turns space on a focused button into a click, so
    // confirming here as well could make one press do two things. But the keeper's
    // main path leaves a button focused every time — .verb and .player-card are both
    // button_tag, and picking the player is a real mouse click, which focuses it — so
    // refusing space there refuses the hundred-times-a-match gesture the strip exists
    // for. Tried it, measured it, took it out: see the space test in
    // test/system/keyboard_shortcuts_test.rb, which fails with such a gate in place.
    $(document).on('keyup', function(e) {
      if(typingInAField(e.target)) return;
      if(document.querySelector('.modal.show')) return;

      if(e.keyCode === 32 && context.waitingForMinute) {    // space
        context.endAction();
        e.preventDefault();
      } else if(e.keyCode === 27) {    // esc
        context.reset();
        e.preventDefault();
      } else if(e.ctrlKey && e.keyCode == 90) {       // ctrl + z
        context.undo();
        e.preventDefault();
      }
    });
  },
  sortByTypeAndNumber: function(pid2data) {
    var arr = [];
    for(pid in pid2data) {
      arr.push( pid2data[pid] );
    }

    return arr.sort(function(a,b) {
      if(a.type == b.type) {
        if(a.number == '00') {
          return -1;
        } else if(b.number == '00') {
          return 1;
        } else {
          return a.number-b.number;
        }
      } else if(a.type == 'home') {
        return -1;
      } else {
        return 1;
      }
    });
  },
  factor: function(diff) {
    return diff < 0 ? -1 : 1;
  },
  field: function(diff) {
    var tmp = Math.abs(diff);
    if(tmp === 0) {
      return "mft";
    } else if(tmp === 1) {
      return "ft";
    } else if(tmp === 2) {
      return "pt2";
    } else if(tmp === 3) {
      return "pt3";
    } else {
      return null;
    }
  },
  player: function(pid) {
    return scoresheet.data.playerId2data[pid];
  },
  // A log entry outlives roster membership. playerId2data holds only the people
  // ON the sheet (Match#players_from selects in_lineup/in_a/in_b), while the log
  // keeps every event ever recorded — so a player taken off the sheet afterwards
  // leaves entries pointing at somebody who is no longer there.
  //
  // The log table binds 301 rows, and one of these throwing takes the WHOLE live
  // scoresheet down mid-match: "Cannot read properties of undefined". A missing
  // name is a display problem; a dead page during a game is not. Same reasoning as
  // the report's technicals table, which refuses to die on a mark it cannot name.
  UNKNOWN_PLAYER: 'neznan igralec',
  playerNameWithNumber: function(pid) {
    var p = scoresheet.player(pid);
    if(!p) return scoresheet.UNKNOWN_PLAYER;

    return p.number + " (" + scoresheet.playerName(pid) + ")";
  },
  name: function(firstName, lastName, lastNameFirst, withInitials, isCaptain) {
    var first = firstName;
    if(withInitials) {
      var tmp = firstName.split(" ");
      var fn = [];
      for(var i = 0; i < tmp.length; i++) {
        if(tmp[i].length == 0) continue;
        fn.push( tmp[i].charAt(0) + "." );
      }
      first = fn.join(" ")
    }
    var k = isCaptain ? " (K)" : "";
    if(lastNameFirst) {
      return lastName.toUpperCase() + " " + first + k;
    } else {
      return first + " " + lastName.toUpperCase() + k;
    }
  },
  playerName: function(pid, lastNameFirst, withInitials) {
    var p = scoresheet.player(pid);
    if(!p) return scoresheet.UNKNOWN_PLAYER;

    // context[pid] is the player's live state and is absent for the same reason
    var state = context[pid];
    return scoresheet.name(p.first_name, p.last_name, lastNameFirst, withInitials, state && state.isCaptain);
  },
  playerType: function(pid) {
    return scoresheet.player(pid).type;
  },
  isOT: function(q) {
    return q > scoresheet.data.numberOfQuarters;
  },
  quarterName: function(q) {
    if(scoresheet.isOT(q)) {
      return (q - scoresheet.data.numberOfQuarters) + ". podaljšek";
    } else {
      return q + ". četrtina"; // TODO: quarter implies four parts
    }
  },
  saveTime: function() {
    $.ajax(scoresheet.data.timeUrl, {
      method: 'GET',
      success: function(data) {
        context.matchFinishedAt = data.timeString;
        scoresheet.save();
      }
    });
  },
  invite: function(email) {
    if(email) {
      $.ajax(scoresheet.data.inviteUrl, {
        method: 'GET',
        data: {
          email: email
        },
        timeout: 10000,
        success: function(data) {
          $('.invite-success-toast').toast('show');
        },
        error: function(err, status, msg) {
          console.error(status, msg, err);
          $('.invite-error-toast').toast('show');
        }
      });
    }
  },
  reportInvite: function() {
    var email = $('#share_report_email').val();
    if(email) {
      $.ajax(scoresheet.data.reportInviteUrl, {
        method: 'GET',
        data: {
          email: email
        },
        timeout: 10000,
        success: function(data) {
          $('.invite-success-toast').toast('show');
          $('#share_report_email').val("");
        },
        error: function(err, status, msg) {
          console.error(status, msg, err);
          $('.invite-error-toast').toast('show');
        }
      });
    }
  },
  localeStorageKey: function() {
    return `scoresheet-${scoresheet.data.matchId}`;
  },
  // Ali predpomnilnik pripada DRUGI tekmi.
  //
  // Ključ je `scoresheet-<ID iz baze>`, ta pa ni stalen: po obnovi baze iz drugega
  // vira lahko isti ID pomeni drugo tekmo. Brskalnik potem prekrije star dnevnik
  // čez novo postavo — pidi ne obstajajo in zaslon umre. fms_id je identiteta, ki
  // to preživi.
  //
  // Zavrže SAMO ob dokazanem neujemanju. Star predpomnilnik brez žiga se obdrži:
  // ta shramba obstaja zato, da zapisnikarju ob izpadu omrežja ne propade delo, in
  // ob uvedbi tega žiga bi ga sicer prav vsem zavrgli naenkrat.
  cacheIsAnotherMatch: function(obj, data) {
    var cached = obj && obj.data ? obj.data.fmsId : null;
    if(!cached || !data.fmsId) return false;

    return String(cached) !== String(data.fmsId);
  },
  getFromLocalStorage: function() {
    return JSON.parse( window.localStorage.getItem(scoresheet.localeStorageKey()) || JSON.stringify({ data: {}, scoresheetObj: {} }) );
  },
  saveToLocalStorage: function(data, now, arhiv) {
    now = now || Date.now();
    data.localStorageSaveTime = now;

    var obj = {
      data: scoresheet.data,
      scoresheetObj: data
    };
    var json = JSON.stringify(obj);

    window.localStorage.setItem(scoresheet.localeStorageKey(), json);
    // The archive (IndexedDB, see openArchive below): every save a version
    // under its timestamp, written only from the PRE-AJAX call (`arhiv`) —
    // that is this client's own outgoing state; the success rewrite only
    // refreshes the working copy's bookkeeping.
    if(arhiv) {
      scoresheet.saveToArchive(data, now);
    }
  },
  // --- the archive -------------------------------------------------------
  // IndexedDB `scoresheet-arhiv`, store `versions`, key [matchId, ts]: EVERY
  // save adds a version and NOTHING ever overwrites or prunes a match's
  // running history — the Hidria–Postojna copy (10. 9. 2026) was lost because
  // a stale page's save REPLACED the only good one, and versions cannot
  // replace each other. IndexedDB rather than localStorage because a full
  // match is ~250 versions x ~35 KB, five times the localStorage quota.
  // Housekeeping runs ACROSS matches only: versions older than 14 days go at
  // init. Nothing here may ever take the keeping down — every failure path is
  // swallowed (old private modes refuse to open the database at all).
  ARCHIVE_DAYS: 14,
  archiveDb: null,
  openArchive: function(cb) {
    if(scoresheet.archiveDb) { cb(scoresheet.archiveDb); return; }
    try {
      var req = window.indexedDB.open('scoresheet-arhiv', 1);
      req.onupgradeneeded = function(e) {
        var store = e.target.result.createObjectStore('versions', { keyPath: ['matchId', 'ts'] });
        store.createIndex('byTs', 'ts');
      };
      req.onsuccess = function(e) {
        scoresheet.archiveDb = e.target.result;
        cb(scoresheet.archiveDb);
      };
      req.onerror = function(e) { e.preventDefault && e.preventDefault(); };
    } catch(e) {}
  },
  saveToArchive: function(data, now) {
    scoresheet.openArchive(function(db) {
      try {
        var tx = db.transaction('versions', 'readwrite');
        // the logs, plus what the replay cannot rebuild from them: the finished
        // flag (replay ignores its log), the captains' complaint, the signature
        // statuses, and the position scalars — together they make a version
        // RESTORABLE (the administer page replays the logs through
        // rebuildFromLogs and saves; these fields are the rest of the document)
        tx.objectStore('versions').put({
          matchId: String(scoresheet.data.matchId), ts: now,
          logs: data.logs, matchFinished: data.matchFinished, complaint: data.complaint,
          referees: data.referees, quarter: data.quarter, minute: data.minute,
          lastPlayedQuarter: data.lastPlayedQuarter,
          firstOffense: data.firstOffense, nextOffense: data.nextOffense
        });
        tx.onerror = function(e) { e.preventDefault && e.preventDefault(); };
      } catch(e) {}
    });
  },
  cleanArchive: function() {
    scoresheet.openArchive(function(db) {
      try {
        var cutoff = Date.now() - scoresheet.ARCHIVE_DAYS * 24 * 3600 * 1000;
        var tx = db.transaction('versions', 'readwrite');
        tx.objectStore('versions').index('byTs').openCursor(window.IDBKeyRange.upperBound(cutoff)).onsuccess = function(e) {
          var cur = e.target.result;
          if(cur) { cur.delete(); cur.continue(); }
        };
        tx.onerror = function(e) { e.preventDefault && e.preventDefault(); };
      } catch(e) {}
    });
  },
  save: function() {
    var timeout = 20000;
    var con = context;
  
    var scoresheetData = JSON.parse( JSON.stringify(con) );
    setTimeout(function() {
      // save to localstorage each time — archive included: this is the
      // client's own outgoing state
      scoresheet.saveToLocalStorage(scoresheetData, null, true);
    }, 50);

    if(con.saving && con.startedSavingTime && con.startedSavingTime + timeout > Date.now()) {      
      con.saveOnSuccess = true;
      return;
    }
    con.saving = true;
    con.startedSavingTime = Date.now();
    
    // save to localstorage before ajax call and set serverSaveTime to now
    
    $.ajax(scoresheet.data.saveUrl, {
      method: 'PUT',
      data: {
        scoresheet: scoresheetData
      },
      timeout: timeout,
      success: function(data) {
        // save success time to localstorage and to context
        var now = Date.now();
        con.serverSaveTime = now;
        con.localStorageSaveTime = now;
        scoresheetData.serverSaveTime = now;
        scoresheet.saveToLocalStorage(scoresheetData, now);

        refreshContext();

        // No success toast. There is one save per action, so this fired ~300 times
        // a match and taught the keeper to dismiss an identically-shaped popup
        // without reading it — including the failure one. The save chip in the
        // match bar carries this state now, permanently and without interrupting.
        scoresheet.data.saveFailureShown = false;
      },
      error: function(err, status, msg) {
        console.error(status, msg, err);
        // First failure only. A dropped connection produces one failure per action
        // until it comes back, and a toast per action is the same noise as above.
        // After this the save chip is what says NI SHRANJENO, and it stays said.
        if(!scoresheet.data.saveFailureShown) {
          scoresheet.data.saveFailureShown = true;
          $('.error-toast').toast('show');
        }
      },
      complete: function() {
        con.saving = false;
        if(con.saveOnSuccess) {
          con.saveOnSuccess = false;
          scoresheet.save();
        }
      }
    });
  },
  updateColors: function(home, away) {
    $('.colorpicker[data-type=home][data-colortype=bg]').val(home.bg);
    $('.colorpicker[data-type=home][data-colortype=text]').val(home.text);
    $('.colorpicker[data-type=away][data-colortype=bg]').val(away.bg);
    $('.colorpicker[data-type=away][data-colortype=text]').val(away.text);

    var homeBgDark = adjust(home.bg, -20);
    var homeBgDarker = adjust(home.bg, -60);
    var awayBgDark = adjust(away.bg, -20);
    var awayBgDarker = adjust(away.bg, -60);

    var whites = ['#ffffff', 'white'];

    var homeBorderColor = whites.indexOf(home.bg) >= 0 ? 'black' : home.bg;
    var awayBorderColor = whites.indexOf(away.bg) >= 0 ? 'black' : away.bg;

    var homeSelectedColor = whites.indexOf(home.text) >= 0 ? 'black' : 'white';
    var homeSelectedBgColor = whites.indexOf(home.text) >= 0 ? 'white' : 'black';

    var awaySelectedColor = whites.indexOf(away.text) >= 0 ? 'black' : 'white';
    var awaySelectedBgColor = whites.indexOf(away.text) >= 0 ? 'white' : 'black';

    var homeText = whites.indexOf(home.text) >= 0 ? home.bg : home.text;
    var awayText = whites.indexOf(away.text) >= 0 ? away.bg : away.text;

    var $style = $('style#colors');
    var html = ".btn-home { color: "+home.text+"; background-color: "+home.bg+"; border-color: "+homeBorderColor+";  }";
    html += ".btn-home:hover { color: "+home.text+"; background-color: "+homeBgDark+"; border-color: "+homeBgDark+";  }";
    html += ".text-home { color: "+homeText+"}";
    html += ".home-entity.entity-selected, .home-entity.entity-selected:hover { color: "+homeSelectedColor+"; background-color: "+homeSelectedBgColor+"; border-color: black;  }";
    // html += ".entity-selected { color: black; background-color: white; border-color: black; }";
    html += ".home-jersey { color: "+home.text+"; fill: "+home.bg+";}"

    html += ".btn-away { color: "+away.text+"; background-color: "+away.bg+"; border-color: "+awayBorderColor+";  }";
    html += ".btn-away:hover { color: "+away.text+"; background-color: "+awayBgDark+"; border-color: "+awayBgDark+";  }";
    html += ".text-away { color: "+awayText+"}";
    html += ".away-entity.entity-selected, .away-entity.entity-selected:hover { color: "+awaySelectedColor+"; background-color: "+awaySelectedBgColor+"; border-color: black;  }";
    html += ".away-jersey { color: "+away.text+"; fill: "+away.bg+";}"

    $style.html(html);
  },
  
  buildEditSelects: function() {
    $('select.edit-select').find('option').remove();
    scoresheet.addOptionsToFilter(".edit-quarter-select", scoresheet.quarterOptions(true));
    scoresheet.addOptionsToFilter(".edit-minute-select", scoresheet.minuteOptions(true));
    scoresheet.addOptionsToFilter(".edit-type-select", scoresheet.typeOptions(true));
    scoresheet.addOptionsToFilter(".edit-home-players-select", scoresheet.playersOptions('home'));
    scoresheet.addOptionsToFilter(".edit-away-players-select", scoresheet.playersOptions('away'));
    scoresheet.addOptionsToFilter(".edit-change-score-23-select", scoresheet.changeScoreOptions([2,3]));
    scoresheet.addOptionsToFilter(".edit-change-ft-select", scoresheet.changeScoreOptions([0,1]));
    scoresheet.addOptionsToFilter(".edit-change-fouls-select", scoresheet.changeFoulsOptions(context.edit && context.edit.log ? context.edit.log.fn : null));
    scoresheet.addOptionsToFilter(".edit-ft-type-select", scoresheet.ftTypesOptions());
  },
  buildNewSelects: function() {
    $('select.new-select').find('option').remove();
    scoresheet.addOptionsToFilter(".new-action-select", scoresheet.fnOptions());
    scoresheet.addOptionsToFilter(".new-quarter-select", scoresheet.quarterOptions());
    scoresheet.addOptionsToFilter(".new-minute-select", scoresheet.minuteOptions());
    scoresheet.addOptionsToFilter(".new-type-select", scoresheet.typeOptions());
    scoresheet.addOptionsToFilter(".new-home-players-select", scoresheet.playersOptions('home'));
    scoresheet.addOptionsToFilter(".new-away-players-select", scoresheet.playersOptions('away'));
    scoresheet.addOptionsToFilter(".new-change-score-23-select", scoresheet.changeScoreOptions([2,3]));
    scoresheet.addOptionsToFilter(".new-change-ft-select", scoresheet.changeScoreOptions([0,1]));
    scoresheet.addOptionsToFilter(".new-change-fouls-select", scoresheet.changeFoulsOptions());
    scoresheet.addOptionsToFilter(".new-ft-type-select", scoresheet.ftTypesOptions());
    scoresheet.addOptionsToFilter(".new-home-coach-and-bench-select", scoresheet.coachAndBenchOptions('home'));
    scoresheet.addOptionsToFilter(".new-away-coach-and-bench-select", scoresheet.coachAndBenchOptions('away'));
  },
  buildFilters: function() {
    $('select.filter').find('option').remove();
    scoresheet.refreshQuarterOptions();
    scoresheet.addOptionsToFilter("#minute-filter", scoresheet.minuteOptions());
    scoresheet.addOptionsToFilter("#state-filter", scoresheet.stateOptions());
    scoresheet.addOptionsToFilter("#type-filter", scoresheet.typeOptions());
    scoresheet.addOptionsToFilter("#number-filter", scoresheet.numberOptions());
    scoresheet.addOptionsToFilter("#entity-filter", scoresheet.entityOptions());
    scoresheet.addOptionsToFilter("#home-players-filter", scoresheet.playersOptions('home'));
    scoresheet.addOptionsToFilter("#away-players-filter", scoresheet.playersOptions('away'));
  },
  addOptionsToFilter: function(selector, options, defaultValue) {
    for(var i = 0; i < options.length; i++) {
      var $option = $('<option>').val(options[i].value).text(options[i].text);
      if(options[i].disabled) {
        $option.attr('disabled','disabled');
      }
      if(options[i].value == defaultValue) {
        $option.attr('selected','selected');
      }
      $(selector).append($option);
    }
  },
  refreshQuarterOptions: function() {
    $('select.filter#quarter-filter').find('option').remove();
    scoresheet.addOptionsToFilter("#quarter-filter", scoresheet.quarterOptions());
  },
  quarterOptions: function(isEdit) {
    var options = isEdit ? [] : [{ text: 'Vsi deli tekme' }];
    var n = context.quarter + (context.matchFinished ? -1 : 0);
    for(var i = 1; i <= n; i++) {
      options.push({ value: i, text: scoresheet.quarterName(i) });
    }
    return options;
  },
  minuteOptions: function(isEdit) {
    var options = isEdit ? [] : [{ text: 'Vse' }];
    var n = 10;
    for(var i = 1; i <= n; i++) {
      options.push({ value: i, text: i+"'" });
    }
    return options;
  },
  fnOptions: function() {
    return[
      { text: 'Vsi dogodki' },
      { text: "────────── METI ──────────", disabled: true },
      { value: "changeScore|2", text: "Koš za 2"},
      { value: "changeScore|3", text: "Koš za 3"},
      { value: "changeScore|1", text: "Zadet prosti met"},
      { value: "changeScore|0", text: "Zgrešen prosti met"},
      { value: "cancelFt", text: "Preklican prosti met"},
      { text: "──────── OS. NAPAKE ──────", disabled: true },
      { value: "changeFouls", text: "Os. napaka brez pr. metov"},
      { value: "changeFTFouls", text: "Os. napaka s pr. meti"},
      { value: "changeFlagrantFouls", text: "Groba napaka"},
      { value: "changeDisruptiveFouls", text: "Taktična napaka"},
      { value: "changeTech1Fouls", text: "Teh. napaka kat. 1"},
      { value: "changeTech2Fouls", text: "Teh. napaka kat. 2"},
      { value: "changeEjections", text: "Izključitev"},
      { value: "changeFights", text: "Pretep"},
      { text: "────────── NAPAKE TRENERJA IN KLOPI ─────────", disabled: true },
      { value: "changeBenchTFouls", text: "Teh. napaka"},
      { value: "changeBenchEjections", text: "Izključitev"},
      { text: "────────── DRUGO ─────────", disabled: true },
      { value: "changeTimeouts", text: "Minuta odmora"},
      { value: "changeQuarter", text: "Konec četrtine"},
      { value: "finishMatch", text: "Konec tekme"},
      { value: "changePlayed", text: "Vstop v igro"}
    ];
  },
  stateOptions: function() {
    var options = [];
    var texts = [];
    for(action in scoresheet.data.logs.actions) {
      var name;
      if(action === 'changeScore') {
        for(var i = 0; i < 4; i++) {
          var text = scoresheet.data.logs.actions[action].name({diff: i});
          options.push({ value: text+"|"+i, text: text });
        }
      } else {
        var text = scoresheet.data.logs.actions[action].name();
        if(texts.indexOf(text) >= 0) continue;
        options.push({ value: text, text: text });
        texts.push(text);
      }
    }
    options.sort(objectSorter('text'));
    return [{ text: 'Vsi dogodki' }].concat(options);
  },
  changeScoreOptions: function(diffs) {
    var options = [];
    for(diff of diffs) {
      var text = scoresheet.data.logs.actions.changeScore.name({diff: diff});
      options.push( { value: diff, text: text } );
    }
    return options;
  },
  changeFoulsActions: function() {
    return ['changeFouls', 'changeFTFouls', 'changeTFouls', 'changeUFouls',
            'changeFlagrantFouls', 'changeDisruptiveFouls', 'changeTech1Fouls', 'changeTech2Fouls',
            'changeEjections', 'changeFights'];
  },
  // The kinds a correction may write TODAY. The pre-split nešportna and tehnična
  // are history: an old entry stays editable (changeFoulsActions above still
  // names them, which is what gates Uredi), and its OWN kind stays in the
  // dropdown — the select is two-way bound, so a missing option would silently
  // rebind the entry to whatever sits first — but they are never offered
  // otherwise.
  changeFoulsOptions: function(currentFn) {
    var options = [];
    for(action of scoresheet.changeFoulsActions()) {
      if(['changeTFouls', 'changeUFouls'].indexOf(action) >= 0 && action !== currentFn) continue;
      var text = scoresheet.data.logs.actions[action].name();
      options.push( { value: action, text: text } );
    }
    return options;
  },
  typeOptions: function(isEdit) {
    var options = isEdit ? [] : [{ text: 'Obe ekipi' }];
    for(obj of [{ value: 'home', text: scoresheet.data.side2type2data.home.name }, { value: 'away', text: scoresheet.data.side2type2data.away.name }]) {
      options.push(obj);
    }
    return options;
  },
  numberOptions: function() {
    var options = [{ text: 'Vse št.' }];
    var numbers = ['00'];
    for(var i = 0; i < 100; i++) numbers.push(""+i);

    var pNumbers = [];
    for(pid in scoresheet.data.playerId2data) {
      var p = scoresheet.player(pid);
      pNumbers.push( p.number + "" );
    }

    for(var i = 0; i < numbers.length; i++) {
      var number = numbers[i];
      if(pNumbers.indexOf(number) >= 0) {
        options.push({ value: number, text: number });
      }
    }
    return options;
  },
  entityOptions: function() {
    var options = [{ text: 'Vsi akterji' }, { value: scoresheet.data.COACH, text: scoresheet.data.logs.coach }, { value: scoresheet.data.ASSISTANT, text: scoresheet.data.logs.assistant }, { value: scoresheet.data.OTHER, text: scoresheet.data.logs.other }];
    var poptions = [];
    for(pid in scoresheet.data.playerId2data) {
      poptions.push({ value: pid, text: scoresheet.playerName(pid, true, false) });
    }
    poptions.sort(objectSorter('text'));
    return options.concat(poptions);
  },
  playersOptions: function(type) {
    var options = [];
    var poptions = [];
    for(pid in scoresheet.data.playerId2data) {
      var data = scoresheet.data.playerId2data[pid];
      if(data.type === type) {
        poptions.push({ value: pid, text: data.number + " - " + scoresheet.playerName(pid, true, false) });
      }
    }
    poptions.sort(objectSorter(function(p) { var num = p.text.split(' - ')[0]; return parseInt(num); }));
    return options.concat(poptions);
  },
  coachAndBenchOptions: function(type) {
    return [{ text: 'Izberi trenerja ali klop' }, { value: scoresheet.data.COACH, text: scoresheet.data.logs.coach }, { value: scoresheet.data.ASSISTANT, text: scoresheet.data.logs.assistant }, { value: scoresheet.data.OTHER, text: scoresheet.data.logs.other }];
  },
  ftTypesOptions: function() {
    var options = [];
    for(type in scoresheet.data.ftType) {
      var obj = scoresheet.data.ftType[type];
      if(obj.key == "separator") {
        options.push({ text: obj.name, disabled: true })
      } else {
        options.push({ value: obj.key, text: obj.name });
      }

    }
    return options;
  }
}
